import { spawn } from "node:child_process";
import { constants, existsSync } from "node:fs";
import { copyFile, mkdir, readdir, readFile, stat, writeFile } from "node:fs/promises";
import { basename, dirname, extname, join, relative } from "node:path";
import { fileURLToPath } from "node:url";
import { loadVortexConfig, parseFrontmatter, serializeFrontmatter } from "@vortex-os/core";
import {
  lintDirectory,
  memoryFrontmatter,
  privacyValid,
  requireFrontmatter,
  wikiLinkResolves,
} from "@vortex-os/data-lint";
import { checkDirectory } from "@vortex-os/link-rewriter";
import { WorklogStore } from "@vortex-os/worklog";
import { checkAddonUpdates } from "../addon-check.js";
import { queryNpmAudit } from "../update-check.js";
import type { Command, CommandInput } from "@vortex-os/slash-commands";
import {
  ensureVortexHooks,
  migrateVortexHookCommands,
  parseSettings,
  serializeSettings,
} from "../ensure-hooks.js";
import {
  LOCAL_BIN_RELPATH,
  ensureStatusline,
  isOurStatuslineCommand,
  statuslineCommandIsLite,
  type StatuslineInstallResult,
} from "../statusline.js";
import {
  applyGlobalSetup,
  recordGlobalSetupDecline,
  isSafeInstanceRoot,
  globalSettingsPath,
} from "../global-setup.js";
import {
  committableUpdatePaths,
  inspectOwnership,
  isVortexScopedLockfileDiff,
  isVortexScopedPackageDiff,
  repairOwnershipManifest,
  runTemplatesUpdate,
  writeOwnershipManifest,
  type VortexUpdateResult,
} from "../update.js";
import { wireAddonMcpServers } from "../mcp-autowire.js";
import { changedFrameworkPaths, commitFrameworkPaths, readHeadFileText } from "../git-commit.js";

/**
 * `/vortex <sub>` — Root command for VortEX instance operations.
 *
 * Subcommands:
 *   init    — first-time setup wizard (user profile + first worklog; detects
 *             external folders in $HOME and surfaces an import hint)
 *   status  — instance state report (counts, profile, latestWorklog, missing)
 *   import  — copy a folder into the instance, auto-classify into 5 categories
 *             or preserve user folder structure, inject frontmatter, scan
 *             wiki-link health via @vortex-os/link-rewriter
 *   doctor  — 8-check health diagnosis (system dirs, profile, indexes,
 *             wiki links, data-lint rules, runbook aging, node version,
 *             git remote)
 *   sync    — framework-developer workflow: git pull → npm install → npm run
 *             build → npm run verify. Stops on first failure with stdout/stderr
 *             tail. End users who consume vortex from npm registry do not need
 *             this — they get pre-built dist via `npm install @vortex-os/*`.
 *   help    — list available subcommands
 */

// Reserved subcommand slot. Adding a future stub-then-promoted subcommand
// (e.g. /vortex export, /vortex link-rewrite) only needs an entry here + the
// `not-implemented` advertisement branch below. The unknown-subcommand branch
// handles user typos either way.
const PLANNED_SUBS = [] as const;
type PlannedSub = (typeof PLANNED_SUBS)[number];

export interface VortexInitResult {
  readonly subcommand: "init";
  readonly status: "completed" | "needs-input" | "already-initialized";
  readonly created: readonly string[];
  readonly nextActions: readonly string[];
  readonly missingInputs?: readonly { name: string; prompt: string }[];
  readonly externalFolders?: readonly {
    readonly path: string;
    readonly basename: string;
    readonly mdCount: number;
  }[];
}

export interface VortexPlannedResult {
  readonly subcommand: PlannedSub | "unknown";
  readonly status: "not-implemented";
  readonly message: string;
}

export interface VortexHelpResult {
  readonly subcommand: "help";
  readonly status: "ok";
  /** `/vortex <sub>` subcommands routed inside this command. */
  readonly subcommands: readonly {
    readonly name: string;
    readonly description: string;
    readonly state: "active" | "planned";
  }[];
  /**
   * Other top-level slash commands shipped by the same plugin
   * (`session-rituals`). Independent commands, not `/vortex` subcommands —
   * surfaced here so users discover the full plugin surface from a single
   * `/vortex help` call instead of needing to know they exist separately.
   */
  readonly siblingCommands: readonly {
    readonly name: string;
    readonly description: string;
  }[];
}

export interface VortexStatusResult {
  readonly subcommand: "status";
  readonly status: "ok" | "uninitialized";
  readonly instance: {
    readonly dataDir: string;
    readonly initialized: boolean;
    readonly profile?: { readonly name?: string; readonly role?: string };
  };
  readonly counts: {
    readonly memory: number;
    readonly worklog: number;
    readonly decisionLog: number;
    readonly runbooks: number;
    readonly hubs: number;
  };
  readonly latestWorklog?: {
    readonly date: string;
    readonly keyword: string;
    readonly path: string;
  };
  readonly missing: readonly string[];
  readonly nextActions: readonly string[];
}

export interface VortexImportResult {
  readonly subcommand: "import";
  readonly status: "completed" | "dry-run" | "needs-input" | "source-missing";
  readonly source?: string;
  readonly totalFiles: number;
  readonly copied: number;
  readonly classified: {
    readonly worklog: number;
    readonly decisionLog: number;
    readonly runbooks: number;
    readonly hubs: number;
    readonly memory: number;
    readonly preserved: number;
  };
  readonly frontmatterInjected: number;
  readonly frontmatterPreserved: number;
  // Non-markdown attachments copied byte-for-byte (preserved layout).
  readonly attachmentsCopied: number;
  // Possible credential files skipped (keys, .env, secrets/credentials dirs)
  // and oversized attachments skipped — surfaced so the user can act. Both are
  // relative-to-source paths; `skippedLarge` entries carry the size.
  readonly skippedSecrets: readonly string[];
  readonly skippedLarge: readonly string[];
  readonly systemDirsCreated: readonly string[];
  readonly skipped: number;
  // Files that were NOT imported because their target path already existed
  // (a basename collision after auto-classification, or a clash with a file
  // already in the instance). Never overwritten — surfaced here so the user
  // can resolve them by hand. `collidedFiles` are relative-to-source paths.
  readonly collisions: number;
  readonly collidedFiles: readonly string[];
  readonly links?: {
    readonly filesScanned: number;
    readonly total: number;
    readonly resolved: number;
    readonly broken: number;
    readonly ambiguous: number;
  };
  readonly missingInputs?: readonly { name: string; prompt: string }[];
  readonly nextActions: readonly string[];
}

export type DoctorCheckStatus = "pass" | "warn" | "fail" | "info";

export interface DoctorCheck {
  readonly id: string;
  readonly label: string;
  readonly status: DoctorCheckStatus;
  readonly detail?: string;
}

export interface VortexDoctorResult {
  readonly subcommand: "doctor";
  readonly status: "ok" | "warnings" | "errors";
  readonly checks: readonly DoctorCheck[];
  readonly summary: {
    readonly pass: number;
    readonly warn: number;
    readonly fail: number;
    readonly info: number;
  };
  readonly nextActions: readonly string[];
}

export type VortexSyncStepId = "pull" | "install" | "build" | "verify";
export type VortexSyncStepStatus = "ok" | "failed" | "skipped";

export interface VortexSyncStep {
  readonly id: VortexSyncStepId;
  readonly label: string;
  readonly status: VortexSyncStepStatus;
  readonly exitCode?: number;
  readonly durationMs?: number;
  readonly stdoutTail?: string;
  readonly stderrTail?: string;
}

export interface VortexSyncResult {
  readonly subcommand: "sync";
  readonly status: "completed" | "failed" | "dry-run";
  readonly steps: readonly VortexSyncStep[];
  readonly failedAt?: VortexSyncStepId;
  readonly nextActions: readonly string[];
}

export interface VortexGlobalSetupResult {
  readonly subcommand: "global-setup";
  readonly status: "completed" | "declined" | "error";
  readonly created: readonly string[];
  readonly modified: readonly string[];
  readonly skipped: readonly string[];
  readonly nextActions: readonly string[];
}

/** `/vortex ontos <check|why>` — the optional ontos add-on's status + decision trail. */
export interface VortexOntosResult {
  readonly subcommand: "ontos";
  readonly status: "ok" | "not-installed" | "no-index" | "error" | "not-implemented";
  readonly action?: "check" | "why";
  readonly counts?: { readonly objects: number; readonly relations: number; readonly proposals: number };
  readonly why?: {
    readonly target: string;
    readonly resolved: string | null;
    readonly steps: readonly unknown[];
    readonly hints: readonly unknown[];
  };
  readonly message: string;
}

export type VortexResult =
  | VortexInitResult
  | VortexStatusResult
  | VortexImportResult
  | VortexDoctorResult
  | VortexSyncResult
  | VortexUpdateResult
  | VortexGlobalSetupResult
  | VortexOntosResult
  | VortexPlannedResult
  | VortexHelpResult;

export const vortexCommand: Command<VortexResult> = {
  name: "vortex",
  description:
    "VortEX root command. Subcommands: init | status | import | doctor | update | sync | help.",
  args: [
    {
      name: "sub",
      description: "Subcommand (init|status|import|doctor|update|sync|help).",
      required: false,
    },
  ],
  handler: async (input: CommandInput): Promise<VortexResult> => {
    // Prefer the caller's pre-split tokens (the CLI threads them via `argv`) over
    // re-tokenizing the joined string — the latter round-trip can mis-split a
    // value that contains a quote/space or is empty. Fall back to `tokenize` for
    // the typed-slash path, where only `rest` is available.
    const tokens = input.argv ?? tokenize(input.rest);
    const sub = (tokens[0] ?? "help") as string;
    const restAfterSub = tokens.slice(1);

    if (sub === "init") return runInit(input, restAfterSub);
    if (sub === "status") return runStatus(input);
    if (sub === "import") return runImport(input, restAfterSub);
    if (sub === "doctor") return runDoctor(input, restAfterSub);
    if (sub === "update") return runUpdate(input, restAfterSub);
    if (sub === "sync") return runSync(input, restAfterSub);
    if (sub === "global-setup") return runGlobalSetup(input, restAfterSub);
    if (sub === "ontos") return runOntos(input, restAfterSub);
    if (sub === "help" || sub === "") return runHelp();
    if ((PLANNED_SUBS as readonly string[]).includes(sub)) {
      return {
        subcommand: sub as PlannedSub,
        status: "not-implemented",
        message:
          `\`/vortex ${sub}\` is reserved but not yet implemented. ` +
          `Planned in a future phase. Run \`/vortex help\` for available subcommands.`,
      };
    }
    return {
      subcommand: "unknown",
      status: "not-implemented",
      message: `Unknown subcommand "${sub}". Run \`/vortex help\` for the list.`,
    };
  },
};

function runHelp(): VortexHelpResult {
  return {
    subcommand: "help",
    status: "ok",
    subcommands: [
      {
        name: "init",
        description:
          "First-time setup wizard. Creates user profile memory and first worklog (hubs grow organically — not seeded here).",
        state: "active",
      },
      {
        name: "status",
        description:
          "Show instance state (memory count, latest worklog, missing skeletons).",
        state: "active",
      },
      {
        name: "import",
        description:
          "Bring an existing folder into data/ — preserves your folder structure, auto-classifies worklog/decision-log/runbooks/hubs/_memory files, injects missing frontmatter.",
        state: "active",
      },
      {
        name: "doctor",
        description:
          "Diagnose instance health (system dirs, profile, indexes, wiki links, frontmatter, runbook freshness, node version, git remote, update ownership manifest, stray control bytes in text files). Pass --repair-manifest to adopt an instance created before the update lifecycle (writes the ownership manifest so /vortex update works).",
        state: "active",
      },
      {
        name: "update",
        description:
          "Refresh framework-owned templates (routers, slash-command prompts, config) from the installed package — hash-guarded so files you edited are never overwritten (a `<file>.new` is written instead). Pass --dry-run to preview. Pass --adopt <path> (repeatable) to force a file you edited back to the template and re-track it (your version is backed up; config is refused). Local; no network.",
        state: "active",
      },
      {
        name: "sync",
        description:
          "Framework-developer workflow: git pull → npm install → npm run build → npm run verify. Stops on first failure. End users on npm registry do not need this.",
        state: "active",
      },
      {
        name: "global-setup",
        description:
          "Enable VortEX from ANY folder: merge a SessionStart/SessionEnd hook + instance pointer into your global ~/.claude (settings.json + vortex-global.json) and add a pointer block to ~/.claude/CLAUDE.md. Merge-safe and idempotent. Pass --decline to dismiss the offer so it stops appearing.",
        state: "active",
      },
      {
        name: "ontos",
        description:
          "Optional ontos add-on — a lightweight ontology of your operational world. `check` (index health), `why <target>` (decision trail + unconfirmed hints). Reindex/enrich run via `npx rebuild-ontos` / `npx enrich-ontos`. Absent if the add-on isn't installed.",
        state: "active",
      },
      { name: "help", description: "Show this list.", state: "active" },
    ],
    siblingCommands: [
      {
        name: "session-start",
        description:
          "Emit a start-of-session report (time + data directory counts + missing dirs).",
      },
      {
        name: "log",
        description:
          "Append a `## <section-title>` section to today's worklog entry (creates the entry first if none exists yet today).",
      },
      {
        name: "decision",
        description:
          "Create a new Decision Log entry from the canonical template at `data/decision-log/<today>-<slug>.md`. Refuses to overwrite an existing file.",
      },
      {
        name: "reindex",
        description:
          "Regenerate _INDEX.md for any configured target directory (or all targets when called with no argument). Idempotent — unchanged indexes are not rewritten.",
      },
    ],
  };
}

interface InitArgs {
  name?: string;
  role?: string;
  task?: string;
  force?: boolean;
}

/**
 * First-worklog seed used when init runs without a `--task`. Name and role
 * identify the operator and stay required; the task is optional because at
 * setup time many users have no concrete first task yet, and forcing one
 * yields throwaway seeds. English by design — this is public framework output.
 */
const DEFAULT_INIT_TASK = "Setting up my VortEX instance";

/**
 * One-shot gate for the DEFAULT statusline wiring (init/update). The settings
 * merge alone cannot honor "delete the statusLine entry to opt out": a deleted
 * entry is indistinguishable from a never-wired one, so a bare re-ensure would
 * resurrect it on every `vortex update`. This state file records "VortEX
 * already offered the bar once for this instance" — once present, init/update
 * never auto-wire again (the explicit `vortex statusline install` keeps
 * working any time). It sits in data/.vortex/ next to the ownership manifest
 * (instance-committed) so the opt-out survives cross-machine syncs. Known
 * residual: a bar wired MANUALLY before any init/update wrote this state can
 * be re-offered once — then the state exists and the opt-out holds.
 */
function statuslineStatePath(ctx: { readonly dataDir: string }): string {
  return join(ctx.dataDir, ".vortex", "statusline.json"); // ctx.dataDir, not a hardcoded data/ — non-default data dirs resolve too
}

async function recordStatuslineOffered(
  ctx: { readonly dataDir: string },
  by: "init" | "update",
  result: string,
): Promise<void> {
  const p = statuslineStatePath(ctx);
  await mkdir(dirname(p), { recursive: true });
  await writeFile(
    p,
    JSON.stringify(
      { schema: "vortex-statusline-state/1", offeredAt: new Date().toISOString(), by, result },
      null,
      2,
    ) + "\n",
    "utf8",
  );
}

/**
 * Resolve the directory that ships the `init` assets (`templates/`). This file
 * is bundled two different ways:
 *   - in the `@vortex-os/session-rituals` package, the emitted file is
 *     `<pkg>/dist/commands/vortex.js`, so `templates/` is at `../../templates`;
 *   - in the `@vortex-os/base` aggregate, every workspace is inlined into a
 *     single `<base>/dist/index.js`, so `templates/` is at `../templates`
 *     (base ships `_publish/templates/`).
 * Probe both so a single code path works from either bundle.
 */
export function resolveTemplatesDir(): string | null {
  const here = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    join(here, "..", "..", "templates"), // session-rituals: dist/commands -> templates
    join(here, "..", "templates"), // base aggregate: dist -> templates
    join(here, "templates"), // defensive: alongside the bundle
  ];
  for (const c of candidates) {
    if (existsSync(join(c, "commands")) || existsSync(join(c, "routers"))) return c;
  }
  return null;
}

/**
 * Copy the agent-mediated slash-command prompts (templates/commands/*.md) into
 * the instance's `.claude/commands/` so the host (Claude Code) exposes /recall,
 * /agenda, … — each is a prompt that runs the `vortex` CLI and lets the agent
 * present/judge the result. Non-destructive: a command file the user already
 * customized is left untouched. Returns the paths written.
 */
async function installCommandTemplates(repoRoot: string, templatesDir: string | null): Promise<string[]> {
  if (!templatesDir) return [];
  const commandsDir = join(templatesDir, "commands");
  if (!existsSync(commandsDir)) return [];
  const destDir = join(repoRoot, ".claude", "commands");
  await mkdir(destDir, { recursive: true });
  const written: string[] = [];
  for (const name of await readdir(commandsDir)) {
    if (!name.endsWith(".md")) continue;
    const dest = join(destDir, name);
    if (existsSync(dest)) continue;
    await copyFile(join(commandsDir, name), dest);
    written.push(dest);
  }
  return written;
}

/**
 * Copy the on-demand agent guides (templates/guides/**, e.g. the first-steps
 * onboarding flow and its role playbooks) into the instance's `.agent/guides/`
 * — the cross-agent shared dir, so Claude/Codex/Gemini/Cursor all reach the
 * same flow. Recursive (guides nest, e.g. `guides/roles/*.md`); non-destructive:
 * a guide file the user already has is left untouched. Returns paths written.
 */
async function installGuideTemplates(repoRoot: string, templatesDir: string | null): Promise<string[]> {
  if (!templatesDir) return [];
  const guidesDir = join(templatesDir, "guides");
  if (!existsSync(guidesDir)) return [];
  const written: string[] = [];
  const walk = async (srcDir: string, destDir: string): Promise<void> => {
    await mkdir(destDir, { recursive: true });
    for (const e of await readdir(srcDir, { withFileTypes: true })) {
      const src = join(srcDir, e.name);
      const dest = join(destDir, e.name);
      if (e.isDirectory()) {
        await walk(src, dest);
      } else if (e.isFile() && e.name.endsWith(".md")) {
        if (existsSync(dest)) continue;
        await copyFile(src, dest);
        written.push(dest);
      }
    }
  };
  await walk(guidesDir, join(repoRoot, ".agent", "guides"));
  return written;
}

/** The shared-rules file plus per-agent router files seeded into the instance root. */
const ROUTER_FILES = [
  "AI-RULES.md",
  "AGENTS.md",
  "CLAUDE.md",
  "GEMINI.md",
  ".cursorrules",
] as const;

/**
 * Seed the shared-rules file plus the per-agent router files into the instance
 * root so any agent host finds VortEX's behavior contract. `AI-RULES.md` is the
 * single source of truth (the full host-agnostic contract); each per-agent entry
 * file holds only that agent's supplements and pulls in `AI-RULES.md` — Claude
 * and Gemini import it automatically (`@AI-RULES.md`), while Codex (via the
 * `AGENTS.md` it auto-loads) and Cursor are instructed to read it first. (The
 * legacy `CODEX.md` and the old singular `AGENT.md` aggregator are no longer
 * shipped.) These are generic templates — the user personalizes them over time.
 * Non-destructive: a file the user already has is left untouched.
 */
async function installRouterTemplates(repoRoot: string, templatesDir: string | null): Promise<string[]> {
  if (!templatesDir) return [];
  const routersDir = join(templatesDir, "routers");
  if (!existsSync(routersDir)) return [];
  const written: string[] = [];
  for (const name of ROUTER_FILES) {
    const src = join(routersDir, name);
    if (!existsSync(src)) continue;
    const dest = join(repoRoot, name);
    if (existsSync(dest)) continue;
    await copyFile(src, dest);
    written.push(dest);
  }
  return written;
}

/**
 * Seed `.agent/vortex.json` (instance auto-record config) from the shipped
 * template, and drop a minimal instance `package.json` with `"type":"module"`
 * if none exists (the bundle is ESM, so the instance must be a module package
 * for `npx vortex` and the hooks to import it). Both non-destructive.
 */
async function seedInstanceConfig(repoRoot: string, templatesDir: string | null): Promise<string[]> {
  const written: string[] = [];

  // .agent/vortex.json
  const agentDir = join(repoRoot, ".agent");
  const vortexJson = join(agentDir, "vortex.json");
  if (!existsSync(vortexJson)) {
    await mkdir(agentDir, { recursive: true });
    const tmpl = templatesDir ? join(templatesDir, "config", "vortex.json") : null;
    if (tmpl && existsSync(tmpl)) {
      await copyFile(tmpl, vortexJson);
    } else {
      await writeFile(
        vortexJson,
        JSON.stringify(
          {
            autoRecord: {
              sessionStart: true,
              worklog: true,
              decision: true,
              ambientRecall: true,
              archive: true,
              archiveCommit: true,
              archiveAtEnd: true,
              archiveRawRetentionDays: 30,
              vectorize: true,
            },
            updates: { check: "session" },
            sync: { autoPush: false },
            environments: [],
          },
          null,
          2,
        ) + "\n",
        "utf8",
      );
    }
    written.push(vortexJson);
  }

  // minimal instance package.json with type:module (only if none exists)
  const pkgPath = join(repoRoot, "package.json");
  if (!existsSync(pkgPath)) {
    await writeFile(
      pkgPath,
      JSON.stringify(
        {
          name: "vortex-instance",
          version: "0.0.0",
          private: true,
          type: "module",
          description: "A VortEX instance (created by `vortex init`).",
        },
        null,
        2,
      ) + "\n",
      "utf8",
    );
    written.push(pkgPath);
  }

  return written;
}

async function runInit(
  input: CommandInput,
  tokens: readonly string[],
): Promise<VortexInitResult> {
  const args = parseInitArgs(tokens);
  const { dataDir, repoRoot } = input.context;
  const templatesDir = resolveTemplatesDir();

  const requiredDirs = ["_memory", "worklog", "decision-log", "hubs", "inbox", "runbooks"];
  for (const d of requiredDirs) {
    const p = join(dataDir, d);
    if (!existsSync(p)) await mkdir(p, { recursive: true });
  }

  // Scaffold the instance shell (independent of the profile): the five neutral
  // per-agent routers, `.agent/vortex.json`, and a minimal ESM `package.json`.
  // All non-destructive — skipped if already present — so they are seeded even
  // when re-running on a partially-set-up clone.
  const scaffolded: string[] = [];
  try {
    scaffolded.push(...(await installRouterTemplates(repoRoot, templatesDir)));
  } catch {
    // a router copy failing should never block init — fall through
  }
  try {
    scaffolded.push(...(await installGuideTemplates(repoRoot, templatesDir)));
  } catch {
    // guide copies are best-effort — update can restore them later
  }
  try {
    scaffolded.push(...(await seedInstanceConfig(repoRoot, templatesDir)));
  } catch {
    // config/package scaffolding is best-effort
  }

  const profilePath = join(dataDir, "_memory", "user_profile.md");
  if (existsSync(profilePath) && !args.force) {
    // Reconcile the ownership manifest even on a re-run so an instance created
    // before the update lifecycle existed gains one (adoption): it records the
    // CURRENT on-disk state — a router the user already edited is marked
    // unmanaged (installedSha256 = null), never falsely claimed as pristine.
    const manifestNotes: string[] = [];
    try {
      const m = await writeOwnershipManifest(input.context, templatesDir);
      if (m) {
        scaffolded.push(m.path);
        manifestNotes.push(`Reconciled ownership manifest (${m.fileCount} framework file(s)) — \`/vortex update\` is now available.`);
      }
    } catch (e) {
      manifestNotes.push(`⚠️ Could not write ownership manifest: ${(e as Error).message}`);
    }
    return {
      subcommand: "init",
      status: "already-initialized",
      created: scaffolded,
      nextActions: [
        `VortEX instance is already initialized (${profilePath} exists).`,
        scaffolded.length > 0
          ? `Seeded ${scaffolded.length} missing scaffolding file(s): ${scaffolded.map((p) => p.replace(repoRoot, ".")).join(", ")}.`
          : "All scaffolding (routers, .agent/vortex.json, package.json) already present.",
        ...manifestNotes,
        "To re-run, pass `--force` (existing user_profile / first worklog will be overwritten).",
        "To check current state, try `/session-start`.",
      ],
    };
  }

  const missing: { name: string; prompt: string }[] = [];
  // name + role are required identity fields — reject whitespace-only answers
  // (a non-empty but blank string would otherwise pass and render a blank field).
  if (!args.name?.trim()) {
    missing.push({
      name: "name",
      prompt:
        'What name or handle should VortEX use for you? (e.g. "Alex" or "team-lead")',
    });
  }
  if (!args.role?.trim()) {
    missing.push({
      name: "role",
      prompt:
        'What is your main role in one word? (e.g. "engineer", "researcher", "writer")',
    });
  }
  // `task` is intentionally NOT collected here: it is optional and defaults to
  // DEFAULT_INIT_TASK when omitted (resolved below). Only name + role block init.

  if (missing.length > 0) {
    return {
      subcommand: "init",
      status: "needs-input",
      created: [],
      missingInputs: missing,
      nextActions: [
        "Ask the user the prompts in `missingInputs`, then re-run with the answers:",
        '  /vortex init --name "<name>" --role "<role>"',
        'Optional: add --task "<one sentence>" to seed your first worklog; omit it and it defaults to "Setting up my VortEX instance".',
        "Optional: append `--force` to overwrite an already-initialized instance.",
      ],
    };
  }

  const today = todayIso();
  // Start from the scaffolding seeded above (routers, .agent/vortex.json,
  // package.json) so the result reflects the full set of files init created.
  const created: string[] = [...scaffolded];
  const scaffoldNotes: string[] = [];
  if (scaffolded.length > 0) {
    const names = scaffolded.map((p) => p.replace(repoRoot, ".").replace(/\\/g, "/"));
    scaffoldNotes.push(`Seeded instance scaffolding: ${names.join(", ")}.`);
  }

  // Normalize the required identity fields (trim the surrounding whitespace the
  // missing-check above already proved is non-blank) and resolve the first-worklog
  // seed: the user's --task if given, else the default.
  const name = args.name!.trim();
  const role = args.role!.trim();
  const task = args.task?.trim() || DEFAULT_INIT_TASK;
  await writeFile(
    profilePath,
    renderUserProfile(name, role, task, today),
    "utf8",
  );
  created.push(profilePath);

  const [year, month] = today.split("-");
  const worklogDir = join(dataDir, "worklog", year!, month!);
  await mkdir(worklogDir, { recursive: true });
  const worklogPath = join(worklogDir, `${today}-vortex-init.md`);
  await writeFile(
    worklogPath,
    renderFirstWorklog(name, role, task, today),
    "utf8",
  );
  created.push(worklogPath);

  // Wire the SessionStart / SessionEnd hooks into .claude/settings.json so the
  // boot report + worklog net fire automatically — without the user knowing any
  // command. Non-destructive: only adds our entries if absent; a malformed
  // settings file is reported (never overwritten) and init still completes.
  // The status bar ships wired-by-default under the same merge-safe rule: a
  // bar the user already configured is KEPT (`vortex statusline install
  // --force` replaces it explicitly; deleting the statusLine entry opts out).
  const hookNotes: string[] = [];
  try {
    const settingsPath = join(input.context.repoRoot, ".claude", "settings.json");
    const existingText = existsSync(settingsPath) ? await readFile(settingsPath, "utf8") : null;
    const { settings, added, alreadyWired } = ensureVortexHooks(parseSettings(existingText), {
      guard: true,
      tick: loadVortexConfig(input.context).autoRecord.archiveTickMinutes > 0,
    });
    // One-shot: only offer the bar if this instance was never offered it
    // before — see statuslineStatePath (deleting the entry then stays deleted).
    let slStatus: StatuslineInstallResult["status"] | "skipped" = "skipped";
    let nextSettings = settings;
    if (!existsSync(statuslineStatePath(input.context))) {
      const directRoot = existsSync(join(input.context.repoRoot, ...LOCAL_BIN_RELPATH))
        ? input.context.repoRoot
        : undefined;
      const sl = ensureStatusline(settings, false, false, directRoot);
      slStatus = sl.status;
      nextSettings = sl.settings;
    }
    if (!alreadyWired || slStatus === "installed") {
      await mkdir(join(input.context.repoRoot, ".claude"), { recursive: true });
      await writeFile(settingsPath, serializeSettings(nextSettings), "utf8");
      if (existingText === null) created.push(settingsPath); // pre-existing file = modified, not created
      if (!alreadyWired) {
        hookNotes.push(
          `Wired ${added.join(" + ")} hook(s) into .claude/settings.json — the VortEX boot report runs automatically at session start` +
            (added.includes("PreToolUse")
              ? ", and the write guard now denies literal control bytes in file writes (remove the PreToolUse group to opt out)."
              : "."),
        );
      }
      if (slStatus === "installed") {
        hookNotes.push(
          "Wired the VortEX status bar (statusLine) into .claude/settings.json — shows model/context/limits/git at a glance (delete the statusLine entry to opt out for good; `vortex statusline install --lite` for the 1-line form).",
        );
      }
    } else {
      hookNotes.push("Session hooks already wired in .claude/settings.json.");
    }
    if (slStatus === "kept-existing") {
      hookNotes.push(
        "Kept your existing statusLine (not VortEX's) — switch with `vortex statusline install --force` if you want the VortEX bar.",
      );
    }
    if (slStatus !== "skipped") {
      await recordStatuslineOffered(input.context, "init", slStatus);
    }
  } catch (e) {
    hookNotes.push(
      `⚠️ Could not wire session hooks automatically: ${(e as Error).message} ` +
        "Copy .claude/settings.example.json to .claude/settings.json by hand to enable the boot report.",
    );
  }

  // Install the agent-mediated slash commands so the host exposes /recall,
  // /agenda, … — prompts that run the `vortex` CLI and let the agent present
  // the result. Non-destructive (keeps any command the user has customized).
  try {
    const cmds = await installCommandTemplates(input.context.repoRoot, templatesDir);
    created.push(...cmds);
    if (cmds.length > 0) {
      hookNotes.push(
        `Installed ${cmds.length} slash command(s) into .claude/commands/ (${cmds.map((c) => "/" + basename(c, ".md")).join(", ")}).`,
      );
    }
  } catch (e) {
    hookNotes.push(`⚠️ Could not install slash commands: ${(e as Error).message}`);
  }

  // Write the update-lifecycle ownership manifest (data/.vortex/ownership.json)
  // from day one: it records, per framework-owned file, the shipped template
  // hash and the hash of the copy we just wrote, so a later `vortex update` can
  // tell a pristine copy (safe to refresh) from one the user edited (never
  // clobber). Best-effort — a failure here never blocks init.
  try {
    const m = await writeOwnershipManifest(input.context, templatesDir);
    if (m) {
      created.push(m.path);
      hookNotes.push(`Wrote ownership manifest tracking ${m.fileCount} framework file(s) (data/.vortex/ownership.json) — enables \`/vortex update\`.`);
    }
  } catch (e) {
    hookNotes.push(`⚠️ Could not write ownership manifest: ${(e as Error).message}`);
  }

  const externalFolders = await detectExternalFolders(input.context.repoRoot);

  const baseNext = [
    `Done. Created ${created.length} files.`,
    ...scaffoldNotes,
    ...hookNotes,
    "Next 3 things you can try right now:",
    "  /log <one-line update>       — append a section to today's worklog",
    "  /decision <slug> <title>     — record a decision",
    "  /session-start               — daily start-of-session report",
    `Open ${worklogPath} to see your first worklog — it already names "${task}".`,
    "Hubs grow organically: once 3+ categories accumulate on the same topic, create `hubs/_HUB-<topic>.md` to cross-link them.",
    "",
    "🌐 Optional — use VortEX from ANY folder, not just this one? Run `vortex global-setup` (adds an instance pointer + session hook to your global ~/.claude, merge-safe). Skip with `vortex global-setup --decline`.",
  ];

  const importPrompt: string[] = [];
  if (externalFolders && externalFolders.length > 0) {
    importPrompt.push("");
    importPrompt.push(
      "📁 Detected existing folders that look like notes/vaults you may want to import:",
    );
    for (const f of externalFolders) {
      importPrompt.push(`  - ${f.path} (${f.mdCount} .md files)`);
    }
    importPrompt.push("  Import any of them now with:");
    for (const f of externalFolders) {
      importPrompt.push(`    /vortex import --from "${f.path}"`);
    }
    importPrompt.push(
      "  (Append --dry-run first to preview without copying. You can also do this later — these folders are not modified.)",
    );
  }

  return {
    subcommand: "init",
    status: "completed",
    created,
    externalFolders,
    nextActions: [...baseNext, ...importPrompt],
  };
}

/**
 * `/vortex global-setup [--decline]` — make this instance reachable from ANY
 * folder by merging the VortEX hooks + an instance pointer into the user's
 * global `~/.claude` config (settings.json + vortex-global.json + a CLAUDE.md
 * pointer block). All writes are merge-safe and idempotent. `--decline` records
 * a dismissal so the session-start offer stops appearing. See `../global-setup`.
 */
async function runGlobalSetup(
  input: CommandInput,
  tokens: readonly string[],
): Promise<VortexGlobalSetupResult> {
  const instanceRoot = input.context.repoRoot;

  if (tokens.includes("--decline")) {
    try {
      const statePath = await recordGlobalSetupDecline();
      return {
        subcommand: "global-setup",
        status: "declined",
        created: [],
        modified: [statePath],
        skipped: [],
        nextActions: [
          "Noted — VortEX will not offer global usage again.",
          "Changed your mind later? Run `vortex global-setup` any time to enable it.",
        ],
      };
    } catch (e) {
      return globalSetupError(`Could not record the decline: ${(e as Error)?.message ?? e}`);
    }
  }

  // Guard: never write a global pointer to anything that is not an absolute,
  // control-char-free, initialized VortEX instance — otherwise every any-folder
  // command would mis-resolve to a bogus path, and a crafted path could corrupt
  // the CLAUDE.md block.
  if (!isSafeInstanceRoot(instanceRoot)) {
    return globalSetupError(
      `Refusing to enable global usage: "${instanceRoot}" is not a valid initialized VortEX instance ` +
        `(need an absolute path containing .agent/vortex.json or data/_memory/user_profile.md, and no newlines). ` +
        `Run this from your instance folder, or pass VORTEX_REPO_ROOT=<instance path>.`,
    );
  }

  try {
    const { created, modified, skipped } = await applyGlobalSetup({ instanceRoot });
    const changed = created.length + modified.length;
    return {
      subcommand: "global-setup",
      status: "completed",
      created,
      modified,
      skipped,
      nextActions: [
        changed > 0
          ? "Global usage enabled — VortEX now works from any folder."
          : "Global usage was already set up — nothing to change.",
        "Open a NEW agent session (in any folder) so the global rules + session hook load.",
        `Records still go to this instance: ${instanceRoot}`,
        "Note: the global session hook fires only where `@vortex-os/base` resolves. If a folder has no local install and the report doesn't appear, run `npm i -g @vortex-os/base` once — the ~/.claude/CLAUDE.md pointer works regardless.",
        "Undo: delete the VortEX block from ~/.claude/CLAUDE.md and the VortEX hooks from ~/.claude/settings.json.",
      ],
    };
  } catch (e) {
    return globalSetupError(
      `Global setup failed: ${(e as Error)?.message ?? e}. Your ~/.claude was not left mis-routing (the pointer is written before the hook).`,
    );
  }
}

function globalSetupError(message: string): VortexGlobalSetupResult {
  return {
    subcommand: "global-setup",
    status: "error",
    created: [],
    modified: [],
    skipped: [],
    nextActions: [message],
  };
}

/**
 * `/vortex update [--templates-only] [--dry-run] [--adopt <path>...]` — refresh
 * framework-owned templates from the currently-installed package, hash-guarded so
 * user edits are never overwritten. `--templates-only` is the only mode implemented
 * today (package upgrade + re-exec + version awareness are later build-order items);
 * it is the default, so a bare `/vortex update` runs it. `--dry-run` previews the
 * plan without writing anything. `--adopt <path>` (repeatable) force-refreshes a
 * named diverged/edited framework file back to the template and re-tracks it (its
 * prior bytes are backed up; user-owned config is refused).
 */
async function runUpdate(
  input: CommandInput,
  tokens: readonly string[],
): Promise<VortexUpdateResult> {
  const dryRun = tokens.includes("--dry-run");
  const adopt = parseAdoptArgs(tokens);
  const templatesDir = resolveTemplatesDir();
  const cfg = loadVortexConfig(input.context);
  // Paths beyond the template set that THIS run wrote (or vetted) and may
  // auto-commit — hook wiring, the statusline offer state, npm package pins.
  // Collected here and committed ONCE at the bottom together with the template
  // changes, so one update leaves one commit. Unlike the template paths, these
  // must land even when the template pass reports `no-manifest`/`no-templates`
  // (the wiring and the pins happen regardless), so the final commit runs for
  // those statuses too.
  const autoCommitExtras: string[] = [];
  let result = await runTemplatesUpdate(input.context, templatesDir, {
    dryRun,
    adopt: adopt.size > 0 ? adopt : undefined,
  });

  // "What changed for you" — a plain-language headline of what this REAL update
  // did to the user's own files, so the value is visible instead of silent (the
  // "I bumped the version but nothing seems different" complaint). Derived only
  // from data this run already has (version delta + refresh/conflict counts) —
  // no changelog shipping needed. Prepended so it leads the notes; skipped on a
  // dry-run (nothing changed) and when no template actually moved.
  if (!dryRun) {
    const s = result.summary;
    const refreshed = s.replaced + s.installed + s.restored + s.adopted;
    if (refreshed > 0) {
      const verNote =
        result.toVersion && result.toVersion !== result.fromVersion
          ? `updated to @vortex-os/base ${result.toVersion} (from ${result.fromVersion ?? "?"}); `
          : "";
      const parts = [
        `${refreshed} framework file${refreshed === 1 ? "" : "s"} refreshed, so your slash-commands, rules, and config now match the installed version`,
      ];
      if (s.conflicts > 0) {
        const one = s.conflicts === 1;
        parts.push(
          `${s.conflicts} file${one ? "" : "s"} you had edited ${one ? "was" : "were"} left untouched — the new version sits beside ${one ? "it" : "them"} as \`.new\` for you to compare`,
        );
      }
      result = {
        ...result,
        nextActions: [
          `What changed for you: ${verNote}${parts.join("; ")}. Nothing you wrote was overwritten.`,
          ...result.nextActions,
        ],
      };
    }
  }

  // Stage 2 — wire installed add-ons' declared MCP servers into the instance `.mcp.json`
  // (one-shot, add-only, reserved-name-guarded). Distinct from the template/hook wiring below;
  // it NEVER auto-commits `.mcp.json` (that is the user's project config — reported, not committed).
  // Best-effort: a failure here never blocks the update. Skipped on `no-templates` (the installed
  // base is incomplete/suspect — don't wire executable config off a broken install).
  if (result.status !== "no-templates") {
    try {
      const wire = await wireAddonMcpServers(input.context, { dryRun });
      if (wire.notes.length > 0) {
        result = { ...result, nextActions: [...result.nextActions, ...wire.notes] };
      }
    } catch (e) {
      result = {
        ...result,
        nextActions: [...result.nextActions, `⚠️ Could not wire add-on MCP servers: ${(e as Error).message}`],
      };
    }
  }

  // Hook wiring is part of the upgrade path: `init` short-circuits on an
  // already-initialized instance, so hooks added in NEWER framework versions
  // (e.g. the 0.12.0 write guard) would otherwise never reach existing
  // instances. Same merge-safe ensure as init — only missing entries are
  // added, user customizations and foreign hooks stay untouched. Never for a
  // dry-run; a malformed settings file is reported, not overwritten.
  // The status bar rides the same path (wired-by-default since 0.14.0): an
  // existing instance gains it on its next `vortex update`, while a bar the
  // user configured themselves is kept untouched.
  if (!dryRun) {
    // Gate for the global migration below: the global file may only move to
    // the current command shape once the INSTANCE hooks verifiably carry it
    // (ensure succeeded or was already wired) — migrating global while the
    // instance write failed would create the same mismatched-commands
    // double-fire this migration exists to prevent, mirrored.
    let instanceHooksOk = false;
    try {
      const settingsPath = join(input.context.repoRoot, ".claude", "settings.json");
      const existingText = existsSync(settingsPath) ? await readFile(settingsPath, "utf8") : null;
      const { settings, added, alreadyWired } = ensureVortexHooks(parseSettings(existingText), {
        guard: true,
        // Wire/unwire the mid-session tick from the CURRENT instance config —
        // enabling it is "set archiveTickMinutes, run `vortex update`".
        tick: cfg.autoRecord.archiveTickMinutes > 0,
      });
      // One-shot: only offer the bar if this instance was never offered it
      // before — a statusLine the user deleted stays deleted on every later
      // update (see statuslineStatePath).
      let slStatus: StatuslineInstallResult["status"] | "skipped" = "skipped";
      let nextSettings = settings;
      let slMigrated = false;
      const slDirectRoot = existsSync(join(input.context.repoRoot, ...LOCAL_BIN_RELPATH))
        ? input.context.repoRoot
        : undefined;
      if (!existsSync(statuslineStatePath(input.context))) {
        // First offer — never wired here before.
        const sl = ensureStatusline(settings, false, false, slDirectRoot);
        slStatus = sl.status;
        nextSettings = sl.settings;
      } else {
        // Already offered: never RE-ADD a deleted (opt-out) bar — but DO migrate
        // an existing OURS-but-outdated command in place to the current cwd-safe
        // form, preserving the user's full/lite choice. We act ONLY when an
        // ours-command is present, so a deleted entry stays deleted and a
        // foreign bar is untouched. This is what carries the cwd-safe upgrade to
        // existing instances automatically on `vortex update`.
        const curCmd = (settings.statusLine as { command?: string } | undefined)?.command;
        if (typeof curCmd === "string" && isOurStatuslineCommand(curCmd)) {
          const sl = ensureStatusline(settings, statuslineCommandIsLite(curCmd), false, slDirectRoot);
          if (sl.status === "installed") {
            slStatus = sl.status;
            nextSettings = sl.settings;
            slMigrated = true;
          }
        }
      }
      // Pre-write cleanliness proof for the auto-commit below: only when
      // `.claude/settings.json` carried NO uncommitted change before this run
      // wrote it (tracked-clean, or not existing yet so we create it whole) is
      // the post-write diff provably machine-made and safe to commit. A
      // pre-dirty file — the user's own uncommitted edit, or an existing
      // never-committed file — is left for the user, with a note.
      const settingsRel = ".claude/settings.json";
      const settingsPreClean =
        changedFrameworkPaths(input.context.repoRoot, [settingsRel]).length === 0;
      if (!alreadyWired || slStatus === "installed") {
        await mkdir(join(input.context.repoRoot, ".claude"), { recursive: true });
        await writeFile(settingsPath, serializeSettings(nextSettings), "utf8");
        const notes: string[] = [];
        if (settingsPreClean) {
          autoCommitExtras.push(settingsRel);
        } else if (cfg.autoRecord.commitFrameworkChanges) {
          notes.push(
            ".claude/settings.json had uncommitted changes from before this update, so the new wiring was NOT auto-committed — review and commit it together with your edits.",
          );
        }
        if (!alreadyWired) {
          notes.push(
            `Wired missing ${added.join(" + ")} hook(s) into .claude/settings.json` +
              (added.includes("PreToolUse")
                ? " — the write guard now denies literal control bytes in file writes (remove the PreToolUse group to opt out)."
                : "."),
          );
        }
        if (slStatus === "installed") {
          notes.push(
            slMigrated
              ? "Updated the VortEX status bar command to the cwd-safe form — it no longer blanks when the agent changes directories. No action needed."
              : "Wired the VortEX status bar (statusLine) into .claude/settings.json — shows model/context/limits/git at a glance (delete the statusLine entry to opt out for good; `vortex statusline install --lite` for the 1-line form).",
          );
        }
        result = {
          ...result,
          nextActions: [...result.nextActions, ...notes],
        };
      }
      if (slStatus !== "skipped") {
        await recordStatuslineOffered(input.context, "update", slStatus);
        // The one-shot offer state rides the combined auto-commit at the
        // bottom — it MUST reach git: an uncommitted state file + a synced
        // settings deletion would resurrect the bar on the other machine. That
        // guarantee still holds on a `no-manifest`/`no-templates` run, because
        // the combined commit (unlike the old template-only one) runs for
        // those statuses too.
        autoCommitExtras.push(
          relative(input.context.repoRoot, statuslineStatePath(input.context)).replace(/\\/g, "/"),
        );
      }
      instanceHooksOk = true;
    } catch (e) {
      result = {
        ...result,
        nextActions: [
          ...result.nextActions,
          `⚠️ Could not verify/wire session hooks: ${(e as Error).message}`,
        ],
      };
    }

    // The GLOBAL hooks (if `vortex global-setup` ever wrote them) must keep
    // matching the instance hooks byte-for-byte: Claude Code dedups identical
    // commands, so leaving the old shape there while the instance migrates
    // would DOUBLE-FIRE every session hook in the instance folder. Migrate-
    // only — legacy VortEX commands are rewritten in place; on a machine that
    // never ran global-setup this file has no VortEX entries and nothing is
    // added. Best-effort: a missing/malformed global file is left alone.
    // Gated on `instanceHooksOk`: see the flag above.
    try {
      const gsPath = globalSettingsPath();
      if (instanceHooksOk && existsSync(gsPath)) {
        const migrated = migrateVortexHookCommands(parseSettings(await readFile(gsPath, "utf8")));
        if (migrated.changed) {
          await writeFile(gsPath, serializeSettings(migrated.settings), "utf8");
          result = {
            ...result,
            nextActions: [
              ...result.nextActions,
              "Updated the global VortEX session hooks (~/.claude/settings.json) to the current command shape, so they keep deduping against the instance hooks.",
            ],
          };
        }
      }
    } catch {
      // best-effort — global-setup can rewire it explicitly at any time
    }

    // The package pins a framework install leaves behind: the agent runs
    // `npm i @vortex-os/...` right before `vortex update`, so that rewrite of
    // package.json / the lockfile is sitting uncommitted right now — the exact
    // residue that diverged two machines' histories on 2026-06-12. Auto-commit
    // it only when the diff PROVABLY stays inside the @vortex-os scope;
    // anything else is (or may contain) the user's own work and is reported
    // instead. npm runs before this command ever starts, so a pre-write
    // cleanliness proof (like settings.json above) is impossible here — the
    // scope test is the safety line.
    if (cfg.autoRecord.commitFrameworkChanges) {
      try {
        const repoRoot = input.context.repoRoot;
        const pkgRel = "package.json";
        const lockRels = ["package-lock.json", "pnpm-lock.yaml", "yarn.lock"];
        const changed = new Set(changedFrameworkPaths(repoRoot, [pkgRel, ...lockRels]));
        const pinNote = (msg: string) => {
          result = { ...result, nextActions: [...result.nextActions, msg] };
        };
        if (changed.has(pkgRel)) {
          const headPkg = readHeadFileText(repoRoot, pkgRel);
          const wtPkg = existsSync(join(repoRoot, pkgRel))
            ? await readFile(join(repoRoot, pkgRel), "utf8")
            : null;
          if (headPkg !== null && wtPkg !== null && isVortexScopedPackageDiff(headPkg, wtPkg)) {
            autoCommitExtras.push(pkgRel);
            // A changed lockfile does NOT ride package.json's verdict blindly —
            // it may carry the user's own pre-existing drift on top of this
            // install. Each one must qualify on its OWN, against a HEAD baseline:
            // only the parseable package-lock.json whose diff is provably
            // @vortex-os-scoped is committed. A lockfile with NO HEAD baseline
            // (brand-new / never tracked) cannot be scope-proven — npm
            // regenerates the WHOLE lockfile from package.json, so a brand-new
            // file encodes the user's entire dependency graph, not just this
            // install — so it is LEFT for the user, never swept wholesale.
            // pnpm/yarn lockfiles (unparsed formats) are likewise reported.
            for (const l of lockRels.filter((x) => changed.has(x))) {
              const headLock = readHeadFileText(repoRoot, l);
              const wtLock = existsSync(join(repoRoot, l))
                ? await readFile(join(repoRoot, l), "utf8")
                : null;
              if (
                l === "package-lock.json" &&
                headLock !== null &&
                wtLock !== null &&
                isVortexScopedLockfileDiff(headLock, wtLock)
              ) {
                autoCommitExtras.push(l);
              } else {
                pinNote(
                  `${l} changed but its scope can't be proven (no committed baseline, or changes beyond the @vortex-os entries) — left for you to commit.`,
                );
              }
            }
          } else {
            pinNote(
              "package.json has uncommitted changes beyond the @vortex-os pins — left for you to commit (the auto-commit only takes provably framework-scoped changes).",
            );
          }
        } else if (changed.has("package-lock.json")) {
          // Lockfile-only change (package.json untouched) — e.g. npm
          // refreshing lockfile metadata. Parse and prove the scope; a change
          // touching anything outside the @vortex-os entries could be the
          // user's own unrelated work, so it is reported, never swept.
          const headLock = readHeadFileText(repoRoot, "package-lock.json");
          const wtLock = existsSync(join(repoRoot, "package-lock.json"))
            ? await readFile(join(repoRoot, "package-lock.json"), "utf8")
            : null;
          if (headLock !== null && wtLock !== null && isVortexScopedLockfileDiff(headLock, wtLock)) {
            autoCommitExtras.push("package-lock.json");
          } else {
            pinNote(
              "package-lock.json changed beyond the @vortex-os entries (package.json untouched) — left for you to commit.",
            );
          }
        } else if (changed.size > 0) {
          // pnpm/yarn lockfile-only change — those formats are not parsed, so
          // the scope cannot be proven. Report, never sweep.
          pinNote(
            `${[...changed].join(", ")} changed without package.json — left for you to commit (cannot prove the change is framework-scoped).`,
          );
        }
      } catch {
        // best-effort: failing to assess the pins must never fail the update
      }
    }
  }

  // Auto-commit everything this update changed — the refreshed templates + the
  // ownership manifest (full runs only) plus the extras collected above (hook
  // wiring, statusline offer state, package pins) — as ONE commit, so an
  // update never leaves an uncommitted trail. Gated by
  // `autoRecord.commitFrameworkChanges` (default on); never for a dry-run;
  // best-effort (a non-git folder / git failure is a silent no-op). Conflicts
  // (`<file>.new`) are intentionally NOT committed. This commit ALSO runs when
  // the template pass reported `no-manifest`/`no-templates` — the extras must
  // land regardless (see the statusline state note above).
  if (dryRun) return result;
  if (!cfg.autoRecord.commitFrameworkChanges) return result;
  const fullRun = result.status !== "no-manifest" && result.status !== "no-templates";
  const candidates = new Set([
    ...(fullRun ? committableUpdatePaths(input.context, result) : []),
    ...autoCommitExtras,
  ]);
  const paths = changedFrameworkPaths(input.context.repoRoot, [...candidates]);
  if (paths.length === 0) return result;
  const extras = new Set(autoCommitExtras);
  const isPinPath = (p: string) =>
    p === "package.json" || p === "package-lock.json" || p === "pnpm-lock.yaml" || p === "yarn.lock";
  const parts: string[] = [];
  if (paths.some((p) => !extras.has(p))) parts.push(`templates to base ${result.toVersion ?? "?"}`);
  if (paths.includes(".claude/settings.json")) parts.push("hook wiring");
  if (paths.some(isPinPath)) parts.push("package pins");
  const message =
    parts.length > 0
      ? `chore(vortex): framework update (${parts.join(" + ")})`
      : "chore(vortex): framework update bookkeeping";
  const commit = commitFrameworkPaths(input.context.repoRoot, paths, message);
  if (!commit.committed) return result;
  return {
    ...result,
    nextActions: [
      ...result.nextActions,
      `Committed the framework changes (${parts.join(", ") || "bookkeeping"}) so nothing is left uncommitted (autoRecord.commitFrameworkChanges; set false to commit these yourself).`,
    ],
  };
}

/**
 * Parse `--adopt <path>` (repeatable) tokens into the POSIX, repoRoot-relative
 * dest paths the ownership manifest uses, so a Windows-style argument
 * (`.claude\commands\recall.md` or a leading `./`) still matches a slot. The
 * value is the dest path shown in the update report. Exported for tests.
 */
export function parseAdoptArgs(tokens: readonly string[]): Set<string> {
  const adopt = new Set<string>();
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i] === "--adopt" && i + 1 < tokens.length) {
      adopt.add(tokens[++i]!.replace(/\\/g, "/").replace(/^\.\//, ""));
    }
  }
  return adopt;
}

function parseInitArgs(tokens: readonly string[]): InitArgs {
  const args: InitArgs = {};
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t === "--force") {
      args.force = true;
      continue;
    }
    if (t === "--name" && i + 1 < tokens.length) {
      args.name = tokens[++i];
      continue;
    }
    if (t === "--role" && i + 1 < tokens.length) {
      args.role = tokens[++i];
      continue;
    }
    if (t === "--task" && i + 1 < tokens.length) {
      args.task = tokens[++i];
      continue;
    }
  }
  return args;
}

/** Quote-aware tokenizer: supports "double" and 'single' quotes. */
function tokenize(s: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i]!)) i++;
    if (i >= s.length) break;
    const ch = s[i]!;
    if (ch === '"' || ch === "'") {
      const quote = ch;
      i++;
      let buf = "";
      while (i < s.length && s[i] !== quote) {
        buf += s[i++];
      }
      if (i < s.length) i++;
      out.push(buf);
    } else {
      let buf = "";
      while (i < s.length && !/\s/.test(s[i]!)) {
        buf += s[i++];
      }
      out.push(buf);
    }
  }
  return out;
}

function renderUserProfile(
  name: string,
  role: string,
  task: string,
  date: string,
): string {
  return `---
name: user-profile
description: Operator profile captured by /vortex init.
type: user
created: ${date}
updated: ${date}
---

# User Profile

- **Name/handle**: ${name}
- **Role**: ${role}
- **Initial focus**: ${task}

This memory was created by \`/vortex init\` on ${date}. Edit freely as your role evolves.
`;
}

function renderFirstWorklog(
  name: string,
  role: string,
  task: string,
  date: string,
): string {
  return `---
type: worklog
status: active
created: ${date}
updated: ${date}
tags: [worklog, onboarding]
---

# ${date} — Getting started with VortEX

> First worklog, created by \`/vortex init\`. ${name} (${role}). Today's focus: ${task}

## What I'm working on

${task}

## Notes

(append more with \`/log <section-title>\`)

## Next

- [ ] Try \`/decision <slug> <title>\` for your first decision record
- [ ] Add a memory by editing \`data/_memory/user_profile.md\` or creating new ones
- [ ] Run \`/session-start\` tomorrow to see your accumulated state
`;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const COUNT_KEY_TO_DIR: Record<keyof VortexStatusResult["counts"], string> = {
  memory: "_memory",
  worklog: "worklog",
  decisionLog: "decision-log",
  runbooks: "runbooks",
  hubs: "hubs",
};

/**
 * `/vortex ontos <check|why ...>` — the OPTIONAL ontos add-on. ontos is detected
 * lazily (`await import` in try/catch), so base works fine without it. This is an
 * isolated command path: a bug here affects only `/vortex ontos`, never the
 * session-start report or other commands. reindex/enrich stay on their own scripts
 * (`npx rebuild-ontos` / `enrich-ontos`); the agent invokes those directly.
 */
async function runOntos(input: CommandInput, rest: readonly string[]): Promise<VortexOntosResult> {
  const sub = (rest[0] ?? "check").trim();
  let ontos: typeof import("@vortex-os/ontos");
  try {
    ontos = await import("@vortex-os/ontos");
  } catch (e) {
    // A module-not-found is the add-on simply not being installed; anything else
    // (a broken add-on, a missing peer like better-sqlite3) is a real error — don't
    // mis-diagnose it as "not installed".
    const code = (e as NodeJS.ErrnoException)?.code;
    if (code === "ERR_MODULE_NOT_FOUND" || code === "MODULE_NOT_FOUND") {
      return {
        subcommand: "ontos",
        status: "not-installed",
        message: "ontos add-on is not installed — `npm i @vortex-os/ontos`, then `npx rebuild-ontos`.",
      };
    }
    return {
      subcommand: "ontos",
      status: "error",
      message: `ontos add-on failed to load: ${(e as Error)?.message ?? String(e)}`,
    };
  }
  const dbPath = join(input.context.dataDir, "_indexes", "ontos.sqlite");
  if (!existsSync(dbPath)) {
    return {
      subcommand: "ontos",
      status: "no-index",
      message: "ontos index is not built yet — run `npx rebuild-ontos` (deterministic, read-only on your records).",
    };
  }
  // Store ops (open/query a possibly-corrupt sqlite) must not escape to the CLI's
  // top-level catch (which would exit 1); convert any failure to a status:"error"
  // result so the agent gets a clean, narratable shape (Codex review).
  let store: InstanceType<typeof ontos.sqlite.OntosStore> | undefined;
  try {
    store = new ontos.sqlite.OntosStore(dbPath);
    if (sub === "check") {
      const counts = store.counts();
      return {
        subcommand: "ontos",
        status: "ok",
        action: "check",
        counts,
        message: `ontos index: ${counts.objects} objects, ${counts.relations} relations, ${counts.proposals} pending proposal(s).`,
      };
    }
    if (sub === "why") {
      const target = rest.slice(1).join(" ").trim();
      if (!target) {
        return { subcommand: "ontos", status: "error", action: "why", message: "usage: /vortex ontos why <object-id-or-name>" };
      }
      const resolved = store.resolveAlias(target) ?? target;
      const steps = ontos.actions.why(store, resolved, 3);
      const hints = ontos.actions.proposalHints(store, resolved);
      return {
        subcommand: "ontos",
        status: "ok",
        action: "why",
        why: { target, resolved, steps, hints },
        message:
          steps.length || hints.length
            ? `decision trail for "${resolved}" — ${steps.length} confirmed step(s), ${hints.length} unconfirmed hint(s).`
            : `no recorded reasoning for "${target}" (the records may not state a typed relation).`,
      };
    }
    return {
      subcommand: "ontos",
      status: "not-implemented",
      message: `unknown ontos subcommand "${sub}". try: check | why <target>. (reindex/enrich run via \`npx rebuild-ontos\` / \`npx enrich-ontos\`.)`,
    };
  } catch (e) {
    return {
      subcommand: "ontos",
      status: "error",
      message: `ontos ${sub} failed (index may be corrupt — try \`npx rebuild-ontos\`): ${(e as Error)?.message ?? String(e)}`,
    };
  } finally {
    // Guard close() too: a throw here would mask the result and escape to the CLI's
    // top-level catch (exit 1) — the very thing this try/catch prevents (Codex r2).
    try {
      store?.close();
    } catch {
      // best-effort — the file lock releases on process exit anyway
    }
  }
}

async function runStatus(input: CommandInput): Promise<VortexStatusResult> {
  const { dataDir } = input.context;
  const profilePath = join(dataDir, "_memory", "user_profile.md");
  const initialized = existsSync(profilePath);

  const counts = {
    memory: await safeCount(join(dataDir, "_memory"), false),
    worklog: await safeCount(join(dataDir, "worklog"), true),
    decisionLog: await safeCount(join(dataDir, "decision-log"), false),
    runbooks: await safeCount(join(dataDir, "runbooks"), false),
    hubs: await safeCount(join(dataDir, "hubs"), false),
  };

  let latestWorklog: VortexStatusResult["latestWorklog"];
  try {
    const store = new WorklogStore(join(dataDir, "worklog"));
    const latest = await store.getLatest();
    if (latest) {
      latestWorklog = {
        date: latest.date,
        keyword: latest.keyword,
        path: latest.path,
      };
    }
  } catch {
    // worklog dir missing or unreadable — leave undefined
  }

  let profile: VortexStatusResult["instance"]["profile"];
  if (initialized) {
    try {
      const raw = await readFile(profilePath, "utf8");
      const { body } = parseFrontmatter<Record<string, unknown>>(raw);
      profile = extractProfile(body);
    } catch {
      // unreadable — leave undefined
    }
  }

  const missing: string[] = [];
  if (!initialized) {
    missing.push("_memory/user_profile.md — run `/vortex init`");
  }
  for (const [key, count] of Object.entries(counts) as [
    keyof typeof counts,
    number,
  ][]) {
    if (count === 0) {
      const dirName = COUNT_KEY_TO_DIR[key];
      const dirPath = join(dataDir, dirName);
      missing.push(
        existsSync(dirPath)
          ? `${dirName}/ is empty`
          : `${dirName}/ does not exist`,
      );
    }
  }

  const nextActions: string[] = [];
  if (!initialized) {
    nextActions.push("Run `/vortex init --name <name> --role <role>` to set up this instance (optional: add --task \"<first focus>\").");
  } else {
    nextActions.push(
      "Run `/session-start` for a fuller session-opening report.",
      "Run `/log <section-title>` to append a section to today's worklog.",
    );
    if (counts.decisionLog === 0) {
      nextActions.push("Try `/decision <slug> <title>` to record your first decision.");
    }
  }

  return {
    subcommand: "status",
    status: initialized ? "ok" : "uninitialized",
    instance: { dataDir, initialized, profile },
    counts,
    latestWorklog,
    missing,
    nextActions,
  };
}

function extractProfile(
  body: string,
): { name?: string; role?: string } | undefined {
  const nameMatch = body.match(/^- \*\*Name\/handle\*\*:\s*(.+)$/m);
  const roleMatch = body.match(/^- \*\*Role\*\*:\s*(.+)$/m);
  if (!nameMatch && !roleMatch) return undefined;
  const out: { name?: string; role?: string } = {};
  if (nameMatch) out.name = nameMatch[1]!.trim();
  if (roleMatch) out.role = roleMatch[1]!.trim();
  return out;
}

async function safeCount(dir: string, recursive: boolean): Promise<number> {
  if (!existsSync(dir)) return 0;
  try {
    return await countMarkdown(dir, recursive);
  } catch {
    return 0;
  }
}

async function countMarkdown(dir: string, recursive: boolean): Promise<number> {
  let total = 0;
  const entries = await readdir(dir, { withFileTypes: true });
  for (const e of entries) {
    if (e.isFile()) {
      if (!e.name.endsWith(".md")) continue;
      if (e.name === "README.md" || e.name === "_INDEX.md" || e.name === "MEMORY.md") {
        continue;
      }
      if (e.name.startsWith("_TEMPLATE")) continue;
      total++;
    } else if (e.isDirectory() && recursive) {
      if (e.name.startsWith(".") || e.name.startsWith("_")) continue;
      total += await countMarkdown(join(dir, e.name), recursive);
    }
  }
  return total;
}

type ImportCategory =
  | "worklog"
  | "decisionLog"
  | "runbooks"
  | "hubs"
  | "memory"
  | "preserved";

interface ImportArgs {
  from?: string;
  dryRun?: boolean;
}

interface ImportStats {
  totalFiles: number;
  copied: number;
  // Non-markdown files (attachments: images, PDFs, docx, …) copied
  // byte-for-byte into the preserved folder layout. Counted separately from
  // `copied` (markdown) because they take a different path: no frontmatter, no
  // auto-classification, a raw `copyFile`.
  attachmentsCopied: number;
  classified: Record<ImportCategory, number>;
  frontmatterInjected: number;
  frontmatterPreserved: number;
  skipped: number;
  // Source files whose target path already existed (basename collision after
  // auto-classification, or a clash with a pre-existing instance file). These
  // are skipped, never overwritten — recorded here so the import can report
  // them. Relative-to-source paths.
  collisions: string[];
  // Files that looked like credential material (keys, .env, secrets/
  // credentials folders) and were NOT copied. A CLI can't prompt, so we skip
  // and surface them. Relative-to-source paths.
  skippedSecrets: string[];
  // Attachments over the size cap, skipped and surfaced. `"<relPath> (N MB)"`.
  skippedLarge: string[];
  // Distinct on-disk extensions (with leading dot, original case) of the
  // attachments actually copied, so the post-import link check can treat them
  // as valid wiki-link targets.
  importedExtensions: Set<string>;
}

const IMPORT_SKIP_DIRS = new Set([
  ".git",
  "node_modules",
  ".vscode",
  ".idea",
  "dist",
  "build",
  ".obsidian",
  ".trash",
]);
const IMPORT_SKIP_FILES = new Set([".DS_Store", "Thumbs.db", ".gitkeep"]);

// Credential material we never copy during import. A CLI can't pause to ask,
// so the safe default is to skip and report (the user can still copy any of
// these in by hand). Best-effort denylist matched case-insensitively against
// the file extension, the exact filename, and any ancestor directory name.
const SECRET_FILE_EXTS = new Set([
  ".key",
  ".pem",
  ".pfx",
  ".p12",
  ".keystore",
  ".jks",
  ".ppk",
  ".asc",
  ".gpg",
  // `.env` as an extension catches `prod.env`, `secrets.env`, etc. (bare
  // `.env` has no extname, so it's matched by name below).
  ".env",
]);
const SECRET_FILE_NAMES = new Set([
  "id_rsa",
  "id_dsa",
  "id_ecdsa",
  "id_ed25519",
  ".npmrc",
  ".netrc",
  ".pgpass",
  ".git-credentials",
  ".htpasswd",
  ".envrc",
  "credentials",
]);
const SECRET_DIR_NAMES = new Set([
  "secrets",
  "credentials",
  ".ssh",
  ".gnupg",
  ".aws",
  ".gpg",
]);
// Attachments larger than this are skipped — a notes tree shouldn't silently
// swallow huge media — and surfaced so the user can move them in deliberately.
const IMPORT_MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MiB

const WORKLOG_FOLDER_NAMES = new Set([
  "worklog",
  "til",
  "daily",
  "journal",
  "diary",
  "daily-notes",
  "logs",
  "log",
  "일지",
  "날짜별",
]);
const DECISION_FOLDER_NAMES = new Set([
  "decision-log",
  "decisions",
  "decision",
]);
const RUNBOOK_FOLDER_NAMES = new Set(["runbooks", "runbook", "sop"]);
const HUB_FOLDER_NAMES = new Set(["hubs", "hub", "_hub"]);
const MEMORY_FOLDER_NAMES = new Set(["_memory", "memory", "memories"]);

const LEGACY_WORKLOG_TYPES = new Set([
  "til",
  "daily",
  "journal",
  "diary",
  "log",
]);

const FILENAME_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:_\d{4})?-/;

function parseImportArgs(tokens: readonly string[]): ImportArgs {
  const args: ImportArgs = {};
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t === "--dry-run") {
      args.dryRun = true;
      continue;
    }
    if (t === "--from" && i + 1 < tokens.length) {
      args.from = tokens[++i];
      continue;
    }
  }
  return args;
}

async function runImport(
  input: CommandInput,
  tokens: readonly string[],
): Promise<VortexImportResult> {
  const args = parseImportArgs(tokens);
  const { dataDir } = input.context;

  const emptyClassified: Record<ImportCategory, number> = {
    worklog: 0,
    decisionLog: 0,
    runbooks: 0,
    hubs: 0,
    memory: 0,
    preserved: 0,
  };

  if (!args.from) {
    return {
      subcommand: "import",
      status: "needs-input",
      totalFiles: 0,
      copied: 0,
      attachmentsCopied: 0,
      skippedSecrets: [],
      skippedLarge: [],
      classified: emptyClassified,
      frontmatterInjected: 0,
      frontmatterPreserved: 0,
      systemDirsCreated: [],
      skipped: 0,
      collisions: 0,
      collidedFiles: [],
      missingInputs: [
        {
          name: "from",
          prompt:
            "Where is the folder you want to import? (absolute path, e.g. C:/Users/me/notes)",
        },
      ],
      nextActions: [
        "Re-run with the path:",
        '  /vortex import --from "<absolute path>"',
        "Optional: append --dry-run to preview without copying.",
      ],
    };
  }

  if (!existsSync(args.from)) {
    return {
      subcommand: "import",
      status: "source-missing",
      source: args.from,
      totalFiles: 0,
      copied: 0,
      attachmentsCopied: 0,
      skippedSecrets: [],
      skippedLarge: [],
      classified: emptyClassified,
      frontmatterInjected: 0,
      frontmatterPreserved: 0,
      systemDirsCreated: [],
      skipped: 0,
      collisions: 0,
      collidedFiles: [],
      nextActions: [
        `Source folder does not exist: ${args.from}`,
        "Check the path and re-run.",
      ],
    };
  }

  const systemDirs = [
    "_memory",
    "worklog",
    "decision-log",
    "runbooks",
    "hubs",
    "inbox",
  ];
  const systemDirsCreated: string[] = [];
  if (!args.dryRun) {
    for (const d of systemDirs) {
      const p = join(dataDir, d);
      if (!existsSync(p)) {
        await mkdir(p, { recursive: true });
        systemDirsCreated.push(d);
      }
    }
  }

  const stats: ImportStats = {
    totalFiles: 0,
    copied: 0,
    attachmentsCopied: 0,
    classified: { ...emptyClassified },
    frontmatterInjected: 0,
    frontmatterPreserved: 0,
    skipped: 0,
    collisions: [],
    skippedSecrets: [],
    skippedLarge: [],
    importedExtensions: new Set<string>(),
  };

  await walkAndImport(args.from, args.from, dataDir, args.dryRun ?? false, stats);

  // After copying, scan the whole dataDir for wiki-link health. Read-only —
  // does not rewrite any links. Case-insensitive because imported content may
  // come from vaults with different filename casing conventions.
  let links: VortexImportResult["links"];
  if (!args.dryRun && (stats.copied > 0 || stats.attachmentsCopied > 0)) {
    try {
      // Imported attachments are valid link targets too — pass their
      // extensions so `[[contract.pdf]]`/`![[diagram.png]]` resolve instead of
      // being reported broken. `.md` is always indexed by the checker.
      const check = await checkDirectory(dataDir, {
        caseInsensitive: true,
        additionalExtensions: [...stats.importedExtensions],
      });
      links = {
        filesScanned: check.filesScanned,
        total: check.totalLinks,
        resolved: check.resolved,
        broken: check.broken.length,
        ambiguous: check.ambiguous.length,
      };
    } catch {
      // Link check is informational only — never let it fail the import.
    }
  }

  const nextActions: string[] = [];
  if (args.dryRun) {
    nextActions.push(
      "This was a dry-run — no files were copied. Re-run without --dry-run to apply.",
    );
  } else {
    nextActions.push("Run `/vortex status` to see the new counts.");
    if (stats.classified.preserved > 0) {
      nextActions.push(
        `${stats.classified.preserved} files preserved your original folder structure (under <dataDir>/<same-paths>).`,
      );
    }
    const autoClassified =
      stats.classified.worklog +
      stats.classified.decisionLog +
      stats.classified.runbooks +
      stats.classified.hubs +
      stats.classified.memory;
    if (autoClassified > 0) {
      nextActions.push(
        `${autoClassified} files auto-classified into vortex categories (worklog/decision-log/runbooks/hubs/_memory).`,
      );
    }
    if (stats.collisions.length > 0) {
      const preview = stats.collisions.slice(0, 10);
      const more = stats.collisions.length - preview.length;
      nextActions.push(
        `${stats.collisions.length} file(s) were NOT imported — their target already existed and was left untouched (no overwrite). ` +
          `This happens when two sources share a filename after auto-classification, or a source matches a file already in your instance. ` +
          `Skipped: ${preview.join(", ")}${more > 0 ? `, …(+${more} more)` : ""}. ` +
          `Rename or move these in the source folder, then re-run the import.`,
      );
    }
    if (stats.attachmentsCopied > 0) {
      nextActions.push(
        `${stats.attachmentsCopied} attachment(s) (non-markdown — images, PDFs, docx, etc.) copied byte-for-byte into the same folder layout, so wiki links pointing at them keep working.`,
      );
    }
    if (links && (links.broken > 0 || links.ambiguous > 0)) {
      nextActions.push(
        `Wiki-link health: ${links.resolved}/${links.total} resolved across ${links.filesScanned} files; ${links.broken} broken, ${links.ambiguous} ambiguous. Review and curate links over time.`,
      );
    } else if (links && links.total > 0) {
      nextActions.push(
        `Wiki-link health: ${links.resolved}/${links.total} resolved across ${links.filesScanned} files — all clean.`,
      );
    }
  }

  // Security/size skips are surfaced in BOTH dry-run and real mode — the user
  // should see what was left behind before relying on the import.
  if (stats.skippedSecrets.length > 0) {
    const preview = stats.skippedSecrets.slice(0, 10);
    const more = stats.skippedSecrets.length - preview.length;
    nextActions.push(
      `${stats.skippedSecrets.length} possible secret file(s) were NOT imported (private keys, .env, secrets/credentials folders, etc.): ` +
        `${preview.join(", ")}${more > 0 ? `, …(+${more} more)` : ""}. ` +
        `If you really want any of these in your notes, copy them in by hand.`,
    );
  }
  if (stats.skippedLarge.length > 0) {
    const preview = stats.skippedLarge.slice(0, 10);
    const more = stats.skippedLarge.length - preview.length;
    nextActions.push(
      `${stats.skippedLarge.length} large file(s) over ${Math.round(IMPORT_MAX_ATTACHMENT_BYTES / (1024 * 1024))} MB were NOT imported: ` +
        `${preview.join(", ")}${more > 0 ? `, …(+${more} more)` : ""}. ` +
        `Move them in manually if you need them tracked here.`,
    );
  }

  return {
    subcommand: "import",
    status: args.dryRun ? "dry-run" : "completed",
    source: args.from,
    totalFiles: stats.totalFiles,
    copied: stats.copied,
    attachmentsCopied: stats.attachmentsCopied,
    skippedSecrets: stats.skippedSecrets,
    skippedLarge: stats.skippedLarge,
    classified: stats.classified,
    frontmatterInjected: stats.frontmatterInjected,
    frontmatterPreserved: stats.frontmatterPreserved,
    systemDirsCreated,
    skipped: stats.skipped,
    collisions: stats.collisions.length,
    collidedFiles: stats.collisions,
    links,
    nextActions,
  };
}

async function walkAndImport(
  rootSource: string,
  currentDir: string,
  dataDir: string,
  dryRun: boolean,
  stats: ImportStats,
): Promise<void> {
  const entries = await readdir(currentDir, { withFileTypes: true });
  for (const e of entries) {
    const sourcePath = join(currentDir, e.name);
    if (e.isDirectory()) {
      if (IMPORT_SKIP_DIRS.has(e.name.toLowerCase())) continue;
      await walkAndImport(rootSource, sourcePath, dataDir, dryRun, stats);
    } else if (e.isFile()) {
      if (IMPORT_SKIP_FILES.has(e.name)) {
        stats.skipped++;
        continue;
      }
      // Never import credential material — applies to markdown and attachments
      // alike (a note inside a `secrets/` folder is just as sensitive). A CLI
      // can't pause to ask, so we skip and surface it.
      const relPath = sourcePath
        .substring(rootSource.length)
        .replace(/^[/\\]/, "");
      if (isSecretPath(relPath, e.name)) {
        stats.skippedSecrets.push(relPath);
        stats.skipped++;
        continue;
      }
      if (!e.name.toLowerCase().endsWith(".md")) {
        // Non-markdown file = attachment (image, PDF, docx, script, …). Copy
        // it byte-for-byte into the preserved layout so wiki links and embeds
        // that point at it keep resolving. No frontmatter, no classification.
        // `.md` is matched case-insensitively so `README.MD` is still imported
        // AS markdown (not raw-copied as a binary attachment).
        await importAttachment(sourcePath, relPath, e.name, dataDir, dryRun, stats);
        continue;
      }
      stats.totalFiles++;

      const raw = await readFile(sourcePath, "utf8");
      const parsed = parseFrontmatter<Record<string, unknown>>(raw);
      const hasFrontmatter = Object.keys(parsed.frontmatter).length > 0;

      const category = classifyFile(
        sourcePath,
        rootSource,
        e.name,
        parsed.frontmatter,
      );
      stats.classified[category]++;

      if (hasFrontmatter) {
        stats.frontmatterPreserved++;
      } else {
        stats.frontmatterInjected++;
      }

      if (!dryRun) {
        const fileStat = await stat(sourcePath);
        const enhanced = enhanceFrontmatter(
          parsed.frontmatter,
          category,
          fileStat.birthtime,
          fileStat.mtime,
          sourcePath,
          rootSource,
        );
        const targetPath = computeTargetPath(
          category,
          sourcePath,
          rootSource,
          dataDir,
          e.name,
        );
        await mkdir(dirname(targetPath), { recursive: true });
        const out = serializeFrontmatter({
          frontmatter: enhanced,
          body: parsed.body,
        });
        // Auto-classification flattens many source files to
        // data/<category>/<basename>, so two sources sharing a basename — or a
        // source colliding with a file already in the instance — would map to
        // the same target. Write with exclusive-create ("wx") so we never
        // overwrite: on collision we skip the file and record it instead of
        // silently clobbering (data loss the user could not see).
        try {
          await writeFile(targetPath, out, { encoding: "utf8", flag: "wx" });
          stats.copied++;
        } catch (e) {
          if ((e as NodeJS.ErrnoException).code === "EEXIST") {
            stats.collisions.push(
              sourcePath.substring(rootSource.length).replace(/^[/\\]/, ""),
            );
          } else {
            throw e;
          }
        }
      }
    }
  }
}

/**
 * Import a single non-markdown file (an attachment). Unlike markdown, it is
 * copied **byte-for-byte** (a utf8 read + re-serialize would corrupt binaries)
 * into the **preserved** layout (no date/type classification, no frontmatter),
 * with `COPYFILE_EXCL` so an existing target is never overwritten. Oversized
 * files are skipped and recorded. (Credential material is filtered out by the
 * caller before we get here, so it applies to markdown too.)
 */
async function importAttachment(
  sourcePath: string,
  relPath: string,
  filename: string,
  dataDir: string,
  dryRun: boolean,
  stats: ImportStats,
): Promise<void> {
  let info;
  try {
    info = await stat(sourcePath);
  } catch {
    // Vanished/unreadable between readdir and stat — count as skipped, not a
    // hard failure (best-effort import).
    stats.skipped++;
    return;
  }
  if (info.size > IMPORT_MAX_ATTACHMENT_BYTES) {
    const mb = Math.round(info.size / (1024 * 1024));
    stats.skippedLarge.push(`${relPath} (${mb} MB)`);
    stats.skipped++;
    return;
  }

  // Eligible attachment: counts toward totalFiles even in a dry-run (mirrors
  // how markdown is counted), and records its extension so the post-import
  // link check treats it as a valid target. `copied`/`attachmentsCopied`
  // stays at the write below, so a dry-run reports 0 copied.
  stats.totalFiles++;
  const ext = extname(filename);
  if (ext) stats.importedExtensions.add(ext);

  if (dryRun) return;

  const targetPath = join(dataDir, relPath);
  await mkdir(dirname(targetPath), { recursive: true });
  try {
    await copyFile(sourcePath, targetPath, constants.COPYFILE_EXCL);
    stats.attachmentsCopied++;
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "EEXIST") {
      stats.collisions.push(relPath);
    } else {
      throw e;
    }
  }
}

/**
 * True when a path looks like credential material we should not copy during
 * import. Best-effort denylist (case-insensitive) over the file extension, the
 * exact filename, and any ancestor directory in the source-relative path.
 */
function isSecretPath(relPath: string, filename: string): boolean {
  const lowerName = filename.toLowerCase();
  if (lowerName === ".env" || lowerName.startsWith(".env.")) return true;
  if (SECRET_FILE_NAMES.has(lowerName)) return true;
  if (SECRET_FILE_EXTS.has(extname(lowerName))) return true;
  const parts = relPath.split(/[/\\]/);
  // Check ancestor directories only (exclude the final filename segment).
  for (let i = 0; i < parts.length - 1; i++) {
    if (SECRET_DIR_NAMES.has(parts[i]!.toLowerCase())) return true;
  }
  return false;
}

function classifyFile(
  sourcePath: string,
  rootSource: string,
  filename: string,
  frontmatter: Record<string, unknown>,
): ImportCategory {
  const type = String(frontmatter.type ?? "").toLowerCase();
  if (type === "worklog" || LEGACY_WORKLOG_TYPES.has(type)) return "worklog";
  if (type === "decision-log" || type === "decision") return "decisionLog";
  if (type === "runbook") return "runbooks";
  if (type === "hub") return "hubs";
  if (type === "memory" || type === "user") return "memory";

  if (filename.startsWith("_HUB-")) return "hubs";
  if (FILENAME_DATE_PATTERN.test(filename)) return "worklog";

  const relPath = sourcePath
    .substring(rootSource.length)
    .replace(/^[/\\]/, "");
  const parts = relPath.split(/[/\\]/).map((p) => p.toLowerCase());
  for (const part of parts) {
    if (WORKLOG_FOLDER_NAMES.has(part)) return "worklog";
    if (DECISION_FOLDER_NAMES.has(part)) return "decisionLog";
    if (RUNBOOK_FOLDER_NAMES.has(part)) return "runbooks";
    if (HUB_FOLDER_NAMES.has(part)) return "hubs";
    if (MEMORY_FOLDER_NAMES.has(part)) return "memory";
  }

  return "preserved";
}

function computeTargetPath(
  category: ImportCategory,
  sourcePath: string,
  rootSource: string,
  dataDir: string,
  filename: string,
): string {
  // computeTargetPath is markdown-only (non-md attachments keep their exact
  // name via importAttachment). Normalize the markdown extension to lowercase
  // `.md` so the link/lint checkers — which match `.md` case-sensitively —
  // actually scan it. Source `README.MD` → `data/README.md`.
  const mdName = withMdExtension(filename);
  if (category === "preserved") {
    const relPath = withMdExtension(
      sourcePath.substring(rootSource.length).replace(/^[/\\]/, ""),
    );
    return join(dataDir, relPath);
  }
  if (category === "worklog") {
    const match = mdName.match(/^(\d{4})-(\d{2})-/);
    if (match) {
      return join(dataDir, "worklog", match[1]!, match[2]!, mdName);
    }
    const d = new Date();
    const y = String(d.getFullYear());
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return join(dataDir, "worklog", y, m, mdName);
  }
  if (category === "decisionLog") return join(dataDir, "decision-log", mdName);
  if (category === "runbooks") return join(dataDir, "runbooks", mdName);
  if (category === "hubs") return join(dataDir, "hubs", mdName);
  if (category === "memory") return join(dataDir, "_memory", mdName);
  return join(dataDir, mdName);
}

/**
 * Normalize a markdown filename/path so its extension is lowercase `.md`
 * (`README.MD` → `README.md`), leaving the rest untouched. Only changes a
 * `.md`-case extension; non-markdown names pass through unchanged.
 */
function withMdExtension(name: string): string {
  const ext = extname(name);
  if (ext.toLowerCase() === ".md") {
    return name.slice(0, name.length - ext.length) + ".md";
  }
  return name;
}

function enhanceFrontmatter(
  frontmatter: Record<string, unknown>,
  category: ImportCategory,
  birthtime: Date,
  mtime: Date,
  sourcePath: string,
  rootSource: string,
): Record<string, unknown> {
  const enhanced: Record<string, unknown> = { ...frontmatter };

  const existingType = String(enhanced.type ?? "").toLowerCase();
  if (!existingType) {
    const typeMap: Record<ImportCategory, string> = {
      worklog: "worklog",
      decisionLog: "decision-log",
      runbooks: "runbook",
      hubs: "hub",
      memory: "memory",
      preserved: "note",
    };
    enhanced.type = typeMap[category];
  } else if (LEGACY_WORKLOG_TYPES.has(existingType)) {
    enhanced.type = "worklog";
  }

  if (Array.isArray(enhanced.tags)) {
    enhanced.tags = enhanced.tags.map((t) => {
      const s = String(t).toLowerCase();
      if (LEGACY_WORKLOG_TYPES.has(s)) return "worklog";
      return t;
    });
  }

  if (!enhanced.created) {
    enhanced.created = formatYmd(birthtime);
  }
  if (!enhanced.updated) {
    enhanced.updated = formatYmd(mtime);
  }

  if (!enhanced.privacy) {
    const relLower = sourcePath
      .substring(rootSource.length)
      .toLowerCase();
    if (
      relLower.includes("personal-records") ||
      relLower.includes("personal_records")
    ) {
      enhanced.privacy = "personal";
    } else {
      enhanced.privacy = "internal";
    }
  }

  return enhanced;
}

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const DOCTOR_SYSTEM_DIRS = [
  "_memory",
  "worklog",
  "decision-log",
  "runbooks",
  "hubs",
] as const;

const RUNBOOK_AGING_DAYS = 90;
const NODE_MIN_MAJOR = 18;

async function runDoctor(
  input: CommandInput,
  tokens: readonly string[] = [],
): Promise<VortexDoctorResult> {
  const { dataDir, repoRoot } = input.context;
  const checks: DoctorCheck[] = [];

  // `--repair-manifest`: bootstrap the ownership manifest for an instance that
  // lacks one (adoption). Runs before the checks so the manifest check below
  // reflects the repaired state. No-op (and reported) when one already exists.
  let repairNote: string | undefined;
  if (tokens.includes("--repair-manifest")) {
    const r = await repairOwnershipManifest(input.context, resolveTemplatesDir());
    repairNote =
      r.status === "created"
        ? `Adopted this instance: wrote ownership manifest tracking ${r.fileCount} framework file(s). \`vortex update\` is now available.`
        : r.status === "already-present"
          ? `Ownership manifest already present (${r.fileCount} file(s) tracked) — left untouched. Use \`vortex update\` to refresh templates.`
          : r.status === "unreadable"
            ? "Ownership manifest exists but is unreadable/corrupt — NOT overwritten. Restore it from data/.vortex/backups/, or delete it and re-run `--repair-manifest`."
            : "Could not write the ownership manifest — no shipped template index found (reinstall @vortex-os/base).";
  }

  checks.push(checkSystemDirs(dataDir));
  checks.push(checkUserProfile(dataDir));
  checks.push(await checkIndexes(dataDir));
  // Attachments (non-md files imported into the tree) are valid wiki-link
  // targets. Collect their extensions once and hand them to both link checks
  // so `[[contract.pdf]]`/`![[diagram.png]]` aren't reported as broken.
  const attachmentExts = await collectAttachmentExtensions(dataDir);
  checks.push(await checkWikilinks(dataDir, attachmentExts));
  checks.push(await checkFrontmatterLint(dataDir, attachmentExts));
  checks.push(await checkRunbookAging(dataDir));
  checks.push(checkNodeVersion());
  checks.push(await checkGitRemote(repoRoot));
  checks.push(await checkOwnershipManifest(input.context));
  checks.push(await checkControlBytes(dataDir));
  checks.push(await checkAddonsBehind(input.context));
  checks.push(await checkDependencyVulnerabilities(input.context));

  const summary = { pass: 0, warn: 0, fail: 0, info: 0 };
  for (const c of checks) summary[c.status]++;

  const status: VortexDoctorResult["status"] =
    summary.fail > 0 ? "errors" : summary.warn > 0 ? "warnings" : "ok";

  const nextActions: string[] = [];
  if (repairNote) nextActions.push(repairNote);
  if (summary.fail > 0) {
    nextActions.push(
      `${summary.fail} check(s) failed. Address them before relying on the instance.`,
    );
  }
  if (summary.warn > 0) {
    nextActions.push(
      `${summary.warn} warning(s). Review the detail lines — most are recoverable in one command.`,
    );
  }
  if (status === "ok") {
    nextActions.push(
      "All checks pass. Run `/vortex status` for activity counts or `/log <section>` to keep working.",
    );
  }

  return { subcommand: "doctor", status, checks, summary, nextActions };
}

/**
 * Report the update ownership manifest's health: present? consistent with disk?
 * `warn` when there is no manifest (adopt with `vortex doctor --repair-manifest`)
 * or a tracked framework file has gone missing (an update would restore it);
 * otherwise `pass`, with a count of stock vs you-edited vs your-own files so the
 * user knows an update will refresh the stock ones and never overwrite edits.
 */
async function checkOwnershipManifest(ctx: CommandInput["context"]): Promise<DoctorCheck> {
  // Pass the templates dir so a pre-v2 (raw-byte hash) manifest is migrated in
  // memory before inspection — otherwise a Windows CRLF checkout over-reports
  // "you-edited" until the first `vortex update`. Read-only: nothing is written.
  const d = await inspectOwnership(ctx, resolveTemplatesDir());
  if (d.malformed) {
    return {
      id: "ownership-manifest",
      label: "update ownership manifest readable",
      status: "warn",
      detail:
        "data/.vortex/ownership.json exists but is unreadable/corrupt. Restore it from " +
        "data/.vortex/backups/, or delete it and run `/vortex doctor --repair-manifest` to re-adopt.",
    };
  }
  if (!d.present) {
    return {
      id: "ownership-manifest",
      label: "update ownership manifest present",
      status: "warn",
      detail:
        "No data/.vortex/ownership.json (this instance predates the update lifecycle). " +
        "Run `/vortex doctor --repair-manifest` to adopt it — required before `/vortex update`.",
    };
  }
  const counts =
    `${d.total} framework file(s) tracked: ${d.pristine} stock, ${d.modified} you-edited, ${d.unmanaged} your-own` +
    (d.missing > 0 ? `, ${d.missing} missing` : "");
  if (d.missing > 0) {
    return {
      id: "ownership-manifest",
      label: "update ownership manifest consistent",
      status: "warn",
      detail: `${counts}. \`/vortex update\` will restore the missing framework file(s).`,
    };
  }
  return {
    id: "ownership-manifest",
    label: "update ownership manifest consistent",
    status: "pass",
    detail: `${counts}. \`/vortex update\` refreshes the stock files and leaves your edits untouched.`,
  };
}

/** Text-file extensions worth scanning for stray control bytes (authored prose/config). */
const CONTROL_SCAN_EXT = new Set([".md", ".json"]);

/**
 * Flag stray control bytes in authored text files under `data/`. A literal
 * control char (e.g. a NUL pasted into worklog prose, or written instead of a
 * `\uXXXX` escape) makes the file "binary" to git/grep/editors and recurs
 * silently. Scans `.md`/`.json` only — binary attachments (.docx/.hwp/…)
 * legitimately contain NUL and are skipped, as are the derived
 * `_session-archive/` and framework `.vortex/` trees. `warn` (recoverable) when
 * any is found, naming the files; `pass` when clean.
 */
async function checkControlBytes(dataDir: string): Promise<DoctorCheck> {
  const offenders: string[] = [];
  const SKIP_DIRS = new Set(["_session-archive", ".vortex", "node_modules", ".git"]);
  async function walk(dir: string): Promise<void> {
    let entries;
    try {
      entries = await readdir(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries) {
      const p = join(dir, e.name);
      if (e.isDirectory()) {
        if (SKIP_DIRS.has(e.name)) continue;
        await walk(p);
      } else if (e.isFile() && CONTROL_SCAN_EXT.has(extname(e.name).toLowerCase())) {
        try {
          const buf = await readFile(p);
          for (let i = 0; i < buf.length; i++) {
            const x = buf[i]!;
            if (x < 32 && x !== 9 && x !== 10 && x !== 13) {
              offenders.push(`${relative(dataDir, p).replace(/\\/g, "/")} @ byte ${i}`);
              break;
            }
          }
        } catch {
          // unreadable — skip
        }
      }
    }
  }
  await walk(dataDir);
  if (offenders.length === 0) {
    return { id: "control-bytes", label: "no stray control bytes in text files", status: "pass" };
  }
  const shown = offenders.slice(0, 5).join("; ");
  const more = offenders.length > 5 ? ` (+${offenders.length - 5} more)` : "";
  return {
    id: "control-bytes",
    label: "no stray control bytes in text files",
    status: "warn",
    detail:
      `Stray control byte(s) in: ${shown}${more}. A literal control char (write it as a \\uXXXX escape, ` +
      "or describe it in words) makes the file binary to git/grep/editors.",
  };
}

function checkSystemDirs(dataDir: string): DoctorCheck {
  const missing = DOCTOR_SYSTEM_DIRS.filter(
    (d) => !existsSync(join(dataDir, d)),
  );
  if (missing.length === 0) {
    return {
      id: "system-dirs",
      label: "vortex system directories present",
      status: "pass",
    };
  }
  return {
    id: "system-dirs",
    label: "vortex system directories present",
    status: "fail",
    detail: `Missing: ${missing.join(", ")}. Run \`/vortex init\` or create them manually.`,
  };
}

function checkUserProfile(dataDir: string): DoctorCheck {
  const profilePath = join(dataDir, "_memory", "user_profile.md");
  if (existsSync(profilePath)) {
    return {
      id: "user-profile",
      label: "user_profile.md exists",
      status: "pass",
    };
  }
  return {
    id: "user-profile",
    label: "user_profile.md exists",
    status: "fail",
    detail:
      "Missing _memory/user_profile.md. Run `/vortex init` to bootstrap the instance.",
  };
}

async function checkIndexes(dataDir: string): Promise<DoctorCheck> {
  const missing: string[] = [];
  for (const d of DOCTOR_SYSTEM_DIRS) {
    const dirPath = join(dataDir, d);
    if (!existsSync(dirPath)) continue;
    const indexPath = join(dirPath, "_INDEX.md");
    if (!existsSync(indexPath)) missing.push(`${d}/_INDEX.md`);
  }
  if (missing.length === 0) {
    return {
      id: "indexes",
      label: "_INDEX.md present in each system directory",
      status: "pass",
    };
  }
  return {
    id: "indexes",
    label: "_INDEX.md present in each system directory",
    status: "warn",
    detail: `Missing: ${missing.join(", ")}. Run \`/reindex\` to generate them.`,
  };
}

/**
 * Collect the distinct non-markdown file extensions present anywhere under
 * `dataDir` (dot-directories like `.git`/`.vortex` excluded), preserving their
 * on-disk case so the case-sensitive link index matches them. These become
 * valid wiki-link targets for the doctor link checks — without them every
 * `[[attachment.pdf]]` would be reported broken. Best-effort: read errors on a
 * subtree are skipped, never thrown.
 */
async function collectAttachmentExtensions(dataDir: string): Promise<string[]> {
  const exts = new Set<string>();
  const stack: string[] = [dataDir];
  while (stack.length > 0) {
    const current = stack.pop()!;
    let entries;
    try {
      entries = await readdir(current, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const e of entries) {
      if (e.isDirectory()) {
        // Skip dot-dirs (`.git`/`.vortex`) and noisy system trees: the session
        // archive holds many `.jsonl` that aren't wiki-link targets, and
        // `node_modules` isn't user content. (The link checkers still walk
        // these for `.md`; we just don't want their extensions polluting the
        // valid-target set or slowing this scan.)
        if (
          e.name.startsWith(".") ||
          e.name === "_session-archive" ||
          e.name === "node_modules"
        ) {
          continue;
        }
        stack.push(join(current, e.name));
      } else if (e.isFile()) {
        const ext = extname(e.name);
        // Exclude markdown (any case) — it's the scan source, not an
        // attachment target. (Imported markdown is normalized to lowercase
        // `.md`; a stray uppercase `.MD` left by other tools isn't scanned by
        // the checkers, a pre-existing case-sensitivity limitation.)
        if (ext && ext.toLowerCase() !== ".md") exts.add(ext);
      }
    }
  }
  return [...exts];
}

async function checkWikilinks(
  dataDir: string,
  additionalExtensions: readonly string[] = [],
): Promise<DoctorCheck> {
  try {
    const result = await checkDirectory(dataDir, {
      caseInsensitive: true,
      additionalExtensions,
    });
    if (result.totalLinks === 0) {
      return {
        id: "wikilinks",
        label: "wiki links resolve",
        status: "pass",
        detail: "No wiki links found.",
      };
    }
    const broken = result.broken.length;
    const ambiguous = result.ambiguous.length;
    if (broken === 0 && ambiguous === 0) {
      return {
        id: "wikilinks",
        label: "wiki links resolve",
        status: "pass",
        detail: `${result.resolved}/${result.totalLinks} resolved across ${result.filesScanned} files.`,
      };
    }
    return {
      id: "wikilinks",
      label: "wiki links resolve",
      status: "warn",
      detail: `${broken} broken, ${ambiguous} ambiguous out of ${result.totalLinks} total. Curate or run a rewrite pass.`,
    };
  } catch (e) {
    return {
      id: "wikilinks",
      label: "wiki links resolve",
      status: "warn",
      detail: `Could not scan: ${(e as Error).message}`,
    };
  }
}

async function checkFrontmatterLint(
  dataDir: string,
  additionalExtensions: readonly string[] = [],
): Promise<DoctorCheck> {
  try {
    const report = await lintDirectory({
      dir: dataDir,
      rules: [
        requireFrontmatter({ required: ["type"] }),
        privacyValid(),
        memoryFrontmatter(),
        wikiLinkResolves({
          searchRoot: dataDir,
          extensions: [".md", ...additionalExtensions],
        }),
      ],
    });
    const errors = report.findings.filter((f) => f.severity === "error").length;
    const warnings = report.findings.filter(
      (f) => f.severity === "warning",
    ).length;
    if (errors === 0 && warnings === 0) {
      return {
        id: "frontmatter-lint",
        label: "frontmatter / privacy / wiki-link rules",
        status: "pass",
        detail: `${report.filesScanned} files scanned, 0 findings.`,
      };
    }
    if (errors > 0) {
      return {
        id: "frontmatter-lint",
        label: "frontmatter / privacy / wiki-link rules",
        status: "fail",
        detail: `${errors} error(s), ${warnings} warning(s) across ${report.filesScanned} files.`,
      };
    }
    return {
      id: "frontmatter-lint",
      label: "frontmatter / privacy / wiki-link rules",
      status: "warn",
      detail: `${warnings} warning(s) across ${report.filesScanned} files.`,
    };
  } catch (e) {
    return {
      id: "frontmatter-lint",
      label: "frontmatter / privacy / wiki-link rules",
      status: "warn",
      detail: `Could not lint: ${(e as Error).message}`,
    };
  }
}

async function checkRunbookAging(dataDir: string): Promise<DoctorCheck> {
  const runbooksDir = join(dataDir, "runbooks");
  if (!existsSync(runbooksDir)) {
    return {
      id: "runbook-aging",
      label: `runbooks tested within ${RUNBOOK_AGING_DAYS} days`,
      status: "pass",
      detail: "No runbooks directory.",
    };
  }
  const stale: string[] = [];
  let total = 0;
  const cutoff = Date.now() - RUNBOOK_AGING_DAYS * 24 * 60 * 60 * 1000;
  try {
    const entries = await readdir(runbooksDir, { withFileTypes: true });
    for (const e of entries) {
      if (!e.isFile() || !e.name.endsWith(".md")) continue;
      if (
        e.name === "README.md" ||
        e.name === "_INDEX.md" ||
        e.name.startsWith("_TEMPLATE")
      ) {
        continue;
      }
      total++;
      const filePath = join(runbooksDir, e.name);
      const raw = await readFile(filePath, "utf8");
      const { frontmatter } = parseFrontmatter<{ last_tested?: string }>(raw);
      if (!frontmatter.last_tested) {
        stale.push(`${e.name} (no last_tested)`);
        continue;
      }
      const testedAt = new Date(String(frontmatter.last_tested)).getTime();
      if (Number.isNaN(testedAt) || testedAt < cutoff) {
        stale.push(`${e.name} (${String(frontmatter.last_tested)})`);
      }
    }
  } catch (e) {
    return {
      id: "runbook-aging",
      label: `runbooks tested within ${RUNBOOK_AGING_DAYS} days`,
      status: "warn",
      detail: `Could not scan: ${(e as Error).message}`,
    };
  }
  if (total === 0 || stale.length === 0) {
    return {
      id: "runbook-aging",
      label: `runbooks tested within ${RUNBOOK_AGING_DAYS} days`,
      status: "pass",
      detail: total === 0 ? "No runbooks." : `${total} runbook(s), all fresh.`,
    };
  }
  return {
    id: "runbook-aging",
    label: `runbooks tested within ${RUNBOOK_AGING_DAYS} days`,
    status: "warn",
    detail:
      `${stale.length}/${total} runbook(s) stale or untested: ${stale.slice(0, 3).join("; ")}${stale.length > 3 ? "…" : ""}. ` +
      `Even rarely-used runbooks stay valuable — re-verify when the environment changes, do not delete.`,
  };
}

function checkNodeVersion(): DoctorCheck {
  const raw = process.version; // e.g. "v22.17.0"
  const match = raw.match(/^v?(\d+)\./);
  if (!match) {
    return {
      id: "node-version",
      label: `node >= ${NODE_MIN_MAJOR}`,
      status: "warn",
      detail: `Could not parse node version "${raw}".`,
    };
  }
  const major = Number.parseInt(match[1]!, 10);
  if (major >= NODE_MIN_MAJOR) {
    return {
      id: "node-version",
      label: `node >= ${NODE_MIN_MAJOR}`,
      status: "pass",
      detail: `Running ${raw}.`,
    };
  }
  return {
    id: "node-version",
    label: `node >= ${NODE_MIN_MAJOR}`,
    status: "fail",
    detail: `Found ${raw}. TypeScript modules require node ${NODE_MIN_MAJOR} or later. Upgrade node.`,
  };
}

/**
 * Add-on freshness: is an installed `@vortex-os/*` add-on behind its latest
 * published version — and does the instance's version pin silently block the
 * update (the 0.x caret trap: `^0.7.0` won't take `0.9.0`)? Read-only,
 * offline-silent, gated on `updates.check` (so "off" = zero network). Never
 * auto-bumps a pin — it only advises.
 */
async function checkAddonsBehind(ctx: Parameters<typeof checkAddonUpdates>[0]): Promise<DoctorCheck> {
  const id = "addons-current";
  const label = "add-on versions (vs npm latest)";
  const config = loadVortexConfig(ctx);
  if (config.updates.check === "off") {
    return { id, label, status: "info", detail: 'Update checks are off (`updates.check: "off"`) — add-on freshness not checked (no network).' };
  }
  let statuses: ReturnType<typeof checkAddonUpdates>;
  try {
    statuses = checkAddonUpdates(ctx);
  } catch {
    statuses = [];
  }
  if (statuses.length === 0) {
    return { id, label, status: "info", detail: "No @vortex-os/* add-ons declared in package.json." };
  }
  const behind = statuses.filter((s) => s.behind);
  if (behind.length === 0) {
    const anyChecked = statuses.some((s) => s.latest !== null);
    return anyChecked
      ? {
          id,
          label,
          status: "pass",
          detail: `All add-ons current: ${statuses.map((s) => s.pkg.replace("@vortex-os/", "") + " " + s.installed).join(", ")}.`,
        }
      : { id, label, status: "info", detail: "Could not reach the registry (offline?) — add-on freshness not verified." };
  }
  const lines = behind.map((s) => {
    const name = s.pkg.replace("@vortex-os/", "");
    const note =
      s.pinBlocks === true
        ? ` — your pin "${s.range}" blocks it; widen it (e.g. "^${s.latest}") then \`npm i\``
        : s.pinBlocks === false
          ? " — `npm i` can update it"
          : "";
    return `${name}: installed ${s.installed}, latest ${s.latest}${note}`;
  });
  return {
    id,
    label,
    status: "warn",
    detail: `${behind.length} add-on(s) behind. ${lines.join(" · ")}. (Read-only — no version changed automatically.)`,
  };
}

async function checkDependencyVulnerabilities(ctx: Parameters<typeof checkAddonUpdates>[0]): Promise<DoctorCheck> {
  const id = "dependency-audit";
  const label = "dependency vulnerabilities (npm audit)";
  const config = loadVortexConfig(ctx);
  if (config.updates.check === "off") {
    return { id, label, status: "info", detail: 'Update checks are off (`updates.check: "off"`) — npm audit not run (no network).' };
  }
  const audit = queryNpmAudit(ctx.repoRoot);
  if (!audit) {
    return { id, label, status: "info", detail: "Could not run npm audit (offline, no npm, or no lockfile) — vulnerabilities not checked." };
  }
  if (audit.total === 0) {
    return { id, label, status: "pass", detail: "npm audit: no known vulnerabilities in the dependency tree." };
  }
  const parts = (
    [
      ["critical", audit.critical],
      ["high", audit.high],
      ["moderate", audit.moderate],
      ["low", audit.low],
    ] as const
  )
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${k}`);
  // `total` includes info-level entries that the breakdown above omits, so fall back to a
  // non-empty label rather than rendering empty parentheses (codex note).
  const breakdown = parts.length > 0 ? parts.join(", ") : "info-level";
  // WARN, never FAIL: most are transitive deps of third-party packages (MCP SDK / ONNX runtime),
  // and `npm audit fix` is the user's call — never auto-run (it rewrites the lockfile).
  return {
    id,
    label,
    status: "warn",
    detail: `npm audit: ${audit.total} vulnerabilit${audit.total === 1 ? "y" : "ies"} (${breakdown}) — run \`npm audit fix\` (review with \`npm audit\` first; \`--force\` may apply breaking upgrades). Usually transitive deps of the MCP SDK / ONNX runtime.`,
  };
}

async function checkGitRemote(repoRoot: string): Promise<DoctorCheck> {
  const gitConfig = join(repoRoot, ".git", "config");
  if (!existsSync(gitConfig)) {
    return {
      id: "git-remote",
      label: "git remote for sync",
      status: "info",
      detail:
        "Not a git repository. VortEX works fine without git, but git + a hosted remote (GitHub, Gitea, GitLab) is recommended if you want to sync across machines (work / home / USB).",
    };
  }
  try {
    const raw = await readFile(gitConfig, "utf8");
    const match = raw.match(/\[remote "origin"\][\s\S]*?url\s*=\s*(.+)/);
    if (!match) {
      return {
        id: "git-remote",
        label: "git remote for sync",
        status: "info",
        detail:
          "Git repo present but no `origin` remote configured. Add one (`git remote add origin <url>`) if you want cross-machine sync.",
      };
    }
    return {
      id: "git-remote",
      label: "git remote for sync",
      status: "pass",
      detail: `origin = ${match[1]!.trim()}`,
    };
  } catch (e) {
    return {
      id: "git-remote",
      label: "git remote for sync",
      status: "info",
      detail: `Could not read .git/config: ${(e as Error).message}`,
    };
  }
}

async function detectExternalFolders(
  excludePath: string,
): Promise<VortexInitResult["externalFolders"]> {
  const home = process.env.HOME ?? process.env.USERPROFILE ?? "";
  if (!home) return undefined;
  const candidates = [
    join(home, "Documents", "obsidian-vault"),
    join(home, "Documents", "notes"),
    join(home, "Documents", "Notebook"),
    join(home, "notes"),
    join(home, "Notes"),
  ];
  const excludeNorm = excludePath.replace(/[/\\]+$/, "");
  const found: { path: string; basename: string; mdCount: number }[] = [];
  for (const candidate of candidates) {
    const candNorm = candidate.replace(/[/\\]+$/, "");
    // Skip if the candidate is the same as, contains, or is contained by the
    // instance — avoid suggesting self-import.
    if (
      candNorm === excludeNorm ||
      candNorm.startsWith(excludeNorm + "/") ||
      candNorm.startsWith(excludeNorm + "\\") ||
      excludeNorm.startsWith(candNorm + "/") ||
      excludeNorm.startsWith(candNorm + "\\")
    ) {
      continue;
    }
    if (!existsSync(candidate)) continue;
    let mdCount = 0;
    try {
      mdCount = await countMarkdown(candidate, true);
    } catch {
      continue;
    }
    if (mdCount === 0) continue;
    found.push({ path: candidate, basename: basename(candidate), mdCount });
  }
  return found.length > 0 ? found : undefined;
}

interface SyncArgs {
  skipPull?: boolean;
  skipInstall?: boolean;
  skipBuild?: boolean;
  skipVerify?: boolean;
  dryRun?: boolean;
}

function parseSyncArgs(tokens: readonly string[]): SyncArgs {
  const args: SyncArgs = {};
  for (const t of tokens) {
    if (t === "--skip-pull") args.skipPull = true;
    else if (t === "--skip-install") args.skipInstall = true;
    else if (t === "--skip-build") args.skipBuild = true;
    else if (t === "--skip-verify") args.skipVerify = true;
    else if (t === "--dry-run") args.dryRun = true;
  }
  return args;
}

interface SyncStepPlan {
  readonly id: VortexSyncStepId;
  readonly label: string;
  readonly cmd: string;
  readonly cmdArgs: readonly string[];
  readonly skip: boolean;
}

async function runSync(
  input: CommandInput,
  tokens: readonly string[],
): Promise<VortexSyncResult> {
  const args = parseSyncArgs(tokens);
  const { repoRoot } = input.context;

  const plan: readonly SyncStepPlan[] = [
    {
      id: "pull",
      label: "git pull",
      cmd: "git",
      cmdArgs: ["pull"],
      skip: args.skipPull ?? false,
    },
    {
      id: "install",
      label: "npm install",
      cmd: "npm",
      cmdArgs: ["install"],
      skip: args.skipInstall ?? false,
    },
    {
      id: "build",
      label: "npm run build",
      cmd: "npm",
      cmdArgs: ["run", "build"],
      skip: args.skipBuild ?? false,
    },
    {
      id: "verify",
      label: "npm run verify",
      cmd: "npm",
      cmdArgs: ["run", "verify"],
      skip: args.skipVerify ?? false,
    },
  ];

  if (args.dryRun) {
    const steps: VortexSyncStep[] = plan.map((p) => ({
      id: p.id,
      label: p.label,
      status: p.skip ? "skipped" : "ok",
    }));
    return {
      subcommand: "sync",
      status: "dry-run",
      steps,
      nextActions: [
        "Dry-run only — no commands executed.",
        "Re-run without --dry-run to apply.",
      ],
    };
  }

  const steps: VortexSyncStep[] = [];
  for (const p of plan) {
    if (p.skip) {
      steps.push({ id: p.id, label: p.label, status: "skipped" });
      continue;
    }
    const result = await runShellCommand(p.cmd, p.cmdArgs, repoRoot);
    const step: VortexSyncStep = {
      id: p.id,
      label: p.label,
      status: result.exitCode === 0 ? "ok" : "failed",
      exitCode: result.exitCode,
      durationMs: result.durationMs,
      stdoutTail: result.stdoutTail,
      stderrTail: result.stderrTail,
    };
    steps.push(step);
    if (step.status === "failed") {
      return {
        subcommand: "sync",
        status: "failed",
        steps,
        failedAt: p.id,
        nextActions: [
          `\`${p.label}\` failed with exit code ${result.exitCode}.`,
          "Review the stdoutTail / stderrTail and fix the underlying issue, then re-run.",
          "You can skip already-passed earlier steps with --skip-pull / --skip-install / --skip-build / --skip-verify.",
        ],
      };
    }
  }

  const ranCount = steps.filter((s) => s.status === "ok").length;
  return {
    subcommand: "sync",
    status: "completed",
    steps,
    nextActions: [
      `All ${ranCount} step(s) passed. Your framework checkout is up-to-date and verified.`,
      "Run `/vortex status` for an instance-side snapshot.",
    ],
  };
}

interface ShellCommandResult {
  exitCode: number;
  durationMs: number;
  stdoutTail: string;
  stderrTail: string;
}

const SYNC_TAIL_LENGTH = 1000;

async function runShellCommand(
  cmd: string,
  cmdArgs: readonly string[],
  cwd: string,
): Promise<ShellCommandResult> {
  const start = Date.now();
  return new Promise<ShellCommandResult>((resolve) => {
    let stdout = "";
    let stderr = "";
    // shell: true is required so that platform-native launchers find
    // batch/cmd wrappers (e.g. npm.cmd on Windows). Inputs come from a
    // hardcoded plan above — no user-supplied strings reach the shell.
    const child = spawn(cmd, [...cmdArgs], { cwd, shell: true });
    child.stdout?.on("data", (chunk: Buffer) => {
      stdout += chunk.toString("utf8");
    });
    child.stderr?.on("data", (chunk: Buffer) => {
      stderr += chunk.toString("utf8");
    });
    child.on("close", (code) => {
      resolve({
        exitCode: code ?? -1,
        durationMs: Date.now() - start,
        stdoutTail: tailString(stdout, SYNC_TAIL_LENGTH),
        stderrTail: tailString(stderr, SYNC_TAIL_LENGTH),
      });
    });
    child.on("error", (err) => {
      resolve({
        exitCode: -1,
        durationMs: Date.now() - start,
        stdoutTail: tailString(stdout, SYNC_TAIL_LENGTH),
        stderrTail: tailString(
          stderr + "\n[spawn error] " + err.message,
          SYNC_TAIL_LENGTH,
        ),
      });
    });
  });
}

function tailString(s: string, n: number): string {
  if (s.length <= n) return s;
  return "..." + s.slice(-n);
}
